/**
 * Admin Dashboard — admin-only live observability tile.
 *
 * Polls the admin summary endpoints every 30s and renders KPIs.
 * Server enforces `username == "startech"` on every endpoint (403 for
 * everyone else). Client-side check decodes the JWT and redirects
 * non-admins back to "/" so they never see an empty UI.
 *
 * Mounted at /admin/dashboard. Not linked from anywhere — only reachable by
 * typing the URL after logging in as startech.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, BarChart3, Database, Gauge,
  Info, Lock, RefreshCcw, Search, ThumbsDown, ThumbsUp, X,
  Trash2, GitBranch, AlertCircle, TrendingUp,
  ShieldCheck, MessageSquare, Target, LineChart, MousePointerClick,
  LogOut,
} from 'lucide-react';
import ControlledDocs from './ControlledDocs';
import { GovernanceTile } from './GovernanceTile';
import AdminGovernance from './AdminGovernance';
import AdminAtlasRepair from './AdminAtlasRepair';

const API = process.env.REACT_APP_BACKEND_URL;

function decodeJwt(token) {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch (_e) {
    return null;
  }
}

// ─── Agent display-name mapping ───────────────────────────────────────────
// Maps the internal trace identifiers to business-friendly labels users would
// recognise. Keep this in sync with the backend `agent_name` strings emitted
// by @trace_agent / emit_agent_trace calls.
//
// SPD v5.70 (2026-07-11) — `INFO_COPY` safeguard: an earlier session
// deleted the definition but left one reference at the correlation
// InfoPanel below. Defining an empty stub here prevents a runtime
// ReferenceError when the operator opens that panel. The panel just shows
// nothing until real copy is wired.
const INFO_COPY = {};

const AGENT_DISPLAY = {
  'production.query_knowledge_graph': 'Pipeline Orchestrator',
  'production.web_search':           'Web Retrieval Agent',
  'production.rag_search':           'Document Retrieval Agent',
  'production.drafter':              'Drafting Agent',
  'production.permission_classify':  'Permission Classifier Agent',
  'production.query_planner':        'Query Planning Agent',
  'v3.coordinator':                  'Orchestration Agent',
  'v3.coordinator.intent':           'Intent Classification Agent',
  'v3.coordinator.disambiguation':   'Clarification (Disambiguation) Agent',
  'v3.tool_router':                  'Tool Router Agent',
};

const AGENT_KIND_DISPLAY = {
  coordinator:      'Orchestration',
  intent:           'Intent Classification',
  disambiguation:   'Clarification',
  tool_routing:     'Tool Routing',
  retrieval:        'Retrieval',
  pipeline:         'Pipeline (whole-query)',
  drafting:         'Drafting',
  permission_gate:  'Permission Classification',
  query_planning:   'Query Planning',
  agent:            'Other',
};

const friendlyAgent = (a) => AGENT_DISPLAY[a] || a;
const friendlyKind = (k) => AGENT_KIND_DISPLAY[k] || k;

// ─── Canonical workflow sequence ───────────────────────────────────────────
// Every tile that lists agents sorts by this order so the same agent appears
// in the same row position across the entire dashboard. Reflects the actual
// production sequence per the v3.2 Agent BRD §5 Build Order, with parallel
// retrieval branches collapsed into a deterministic order (web before doc,
// alphabetical within a parallel group).
const AGENT_SEQUENCE_ORDER = [
  // Pre-flight: orchestration and gating (runs first)
  'production.query_knowledge_graph',
  'v3.coordinator',
  'production.permission_classify',
  'v3.coordinator.intent',
  'v3.coordinator.disambiguation',
  // Planning: route + plan
  'production.query_planner',
  'v3.tool_router',
  // Retrieval (parallel in production — listed alphabetically for stability)
  'production.rag_search',
  'production.web_search',
  // Drafting + verification (sequential after retrieval)
  'production.drafter',
];

const AGENT_KIND_SEQUENCE_ORDER = [
  'pipeline',          // pipeline-wide trace
  'coordinator',       // orchestration kickoff
  'permission_gate',   // gate
  'intent',            // classify
  'disambiguation',    // clarify
  'query_planning',    // plan
  'tool_routing',      // route
  'retrieval',         // fetch (web + doc, parallel)
  'ranking',           // rank
  'evidence',          // assemble
  'drafting',          // draft
  'verification',      // verify
  'agent',             // unlabelled / fallback
];

// Sort helper: rows the canonical order knows about come first (in canonical
// order); unknown rows are appended alphabetically at the end. Pure function.
const sortBySequence = (rows, key, sequence) => {
  const idx = (v) => {
    const i = sequence.indexOf(v);
    return i === -1 ? sequence.length : i;
  };
  return [...rows].sort((a, b) => {
    const ai = idx(a[key]);
    const bi = idx(b[key]);
    if (ai !== bi) return ai - bi;
    // tie-break alphabetically by friendly name for stability
    return String(a[key] || '').localeCompare(String(b[key] || ''));
  });
};

// Traffic-light evaluation. `direction='up'` = higher is better.
function evalLight(value, { greenAt, yellowAt, direction = 'up' }) {
  if (value == null || Number.isNaN(value)) return { color: 'slate', label: 'No data' };
  if (direction === 'up') {
    if (value >= greenAt) return { color: 'emerald', label: 'Healthy' };
    if (value >= yellowAt) return { color: 'amber', label: 'Watch' };
    return { color: 'rose', label: 'At risk' };
  }
  if (value <= greenAt) return { color: 'emerald', label: 'Healthy' };
  if (value <= yellowAt) return { color: 'amber', label: 'Watch' };
  return { color: 'rose', label: 'At risk' };
}

const LIGHT_DOT = { emerald: 'bg-emerald-400', amber: 'bg-amber-400', rose: 'bg-rose-400', slate: 'bg-slate-600' };
const LIGHT_TEXT = { emerald: 'text-emerald-400', amber: 'text-amber-400', rose: 'text-rose-400', slate: 'text-slate-400' };

// ─── Per-metric definition + how-it's-calculated + thresholds + recommendations
// Edit this object to update help text — no other code changes needed.
const METRIC_INFO = {
  cohesion: {
    definition: 'Percentage of agent handoffs that produced aligned outputs, over the last 7 days. Latency shown separately.',
    howCalculated: 'Per query: (aligned handoffs − errors − unresolved conflicts − redundant steps) ÷ total handoffs. The tile shows the average across all queries in the rolling 7-day window. NOTE: this score measures CORRECTNESS ONLY — it does not include how long each handoff took. For speed, see the "Latency" tile and the "Avg latency / P95 latency" stats within the Cohesion tile.',
    thresholds: [
      { color: 'emerald', label: '≥ 0.80', meaning: 'Agents are collaborating cleanly. No action needed.' },
      { color: 'amber', label: '0.50 – 0.79', meaning: 'Friction is emerging. Check the per-agent-role tile to find which role is dragging.' },
      { color: 'rose', label: '< 0.50', meaning: 'Agents are fighting / erroring / duplicating work. Beta UX is degraded.' },
    ],
    recommendations: [
      'Open the "Per-Agent-Role Cohesion" tile to find the lowest-scoring agent role.',
      'Open the "Recent Pipeline Errors" tile to see what specifically broke.',
      'If the orchestration agent itself is at fault, run `pytest backend/tests/v3/test_coordinator.py` to confirm it isn\'t a regression.',
    ],
  },
  permissions: {
    definition: 'Privacy-by-design metric. Search.Inc only uses the data types each user has explicitly enabled. This score tracks how often we have to stop a query because the data type it would need is currently turned off.',
    howCalculated: 'Permission-blocked events ÷ total permission-gate events over 7 days. The "Top missing" rows below show which specific data types are most often blocking queries (e.g. location, social, media).',
    thresholds: [
      { color: 'emerald', label: '< 20%', meaning: 'Most queries flow through without prompts. Healthy onboarding.' },
      { color: 'amber', label: '20% – 49%', meaning: 'Users frequently bumping into the toggle prompt — onboarding UX may need work.' },
      { color: 'rose', label: '≥ 50%', meaning: 'Either the gate is over-triggering, or users have most toggles off. Friction is killing engagement.' },
    ],
    recommendations: [
      'If one toggle dominates the "Missing" list (e.g. location), consider a one-time onboarding nudge to enable it.',
      'If the classifier is over-claiming required permissions, review `v2/agents/permission_gate.py:classify_query_requirements`.',
      'For repeat blocks on the same user, surface a "Permission summary" UI inside the app so they can self-fix in one click.',
    ],
  },
  retrieval: {
    definition: 'Where answers come from. Two signals: (1) how many user-uploaded document chunks we\'ve indexed for semantic search, and (2) which sources (Web, your Documents, our Knowledge Graph) were used most across recent queries.',
    howCalculated: 'Vector index count is a direct count of registered embeddings. Source mix is parsed from the Tool Router decisions made for each query.',
    thresholds: [
      { color: 'emerald', label: 'Indexed > 0 AND ≥ 2 sources used', meaning: 'Local Knowledge Graph + Tool Router both healthy.' },
      { color: 'amber', label: 'Indexed flat OR one source dominates 90%+', meaning: 'Either ingestion stalled, or queries skew to a single source.' },
      { color: 'rose', label: 'Indexed = 0 after uploads, OR no routes recorded', meaning: 'Ingestion pipeline broken OR Orchestration agent is silently failing.' },
    ],
    recommendations: [
      'If indexed = 0 after Beta uploads: run the one-shot backfill (`embed_all_documents`) to register existing docs.',
      'If web search dominates 90%+: confirm users have the right toggles on; check that `kg_lookup` is reaching the router.',
      'If routes are missing entirely: Orchestration Agent is failing — `tail -n 100 /var/log/supervisor/backend.err.log` for "V3Coordinator failed".',
    ],
  },
  feedback: {
    definition: 'Direct subjective signal from users. Every AI answer can be rated thumbs up / thumbs down, with optional written feedback. The strongest QC signal we have because it captures whether the answer actually solved the user\'s problem.',
    howCalculated: 'Raw counts of up vs down ratings, plus the subset of negative ratings that include written feedback. Stored in `answer_feedback`.',
    thresholds: [
      { color: 'emerald', label: '≥ 80% positive', meaning: 'Users find answers useful. Keep current prompts.' },
      { color: 'amber', label: '60% – 79% positive', meaning: 'Acceptable, but answer quality is wobbly. Worth investigating top patterns.' },
      { color: 'rose', label: '< 60% positive', meaning: 'Quality problem. Beta credibility is at stake.' },
    ],
    recommendations: [
      'Read negatives directly: `db.answer_feedback.find({rating:"down", comment:{$ne:""}}).limit(20)` shows raw user complaints.',
      'Cross-reference low-rated workflows with their cohesion score to see if it\'s answer quality vs. agent coordination.',
      'If a single intent (e.g. "knowledge") owns most downs, tune that prompt template.',
    ],
  },
  latency: {
    definition: 'How long each individual AI agent takes to respond, on average. Lets us spot which agent is the slowest piece of the chain when overall response times feel sluggish.',
    howCalculated: 'Average wall-clock time from agent invocation to return, in milliseconds, grouped by agent. Each row is colour-coded by its own latency.',
    thresholds: [
      { color: 'emerald', label: 'Avg < 2000 ms', meaning: 'Snappy. Beta users feel responsive.' },
      { color: 'amber', label: '2000 – 5000 ms', meaning: 'Acceptable but noticeable. Consider caching frequent results.' },
      { color: 'rose', label: '> 5000 ms', meaning: 'LLM timeouts likely; retries piling up. Users abandoning queries.' },
    ],
    recommendations: [
      'Identify the slowest agent — usually the Drafting or Web Retrieval agent.',
      'Check the Emergent LLM key budget — running low forces fallback to slower providers.',
      'Frequent identical queries can be memoised in `db.query_cache`.',
    ],
  },
  agentKind: {
    definition: 'Reliability of each agent role (Orchestration, Intent Classification, Retrieval, Drafting, etc). Tells us which role to investigate when the overall Cohesion Score drops.',
    howCalculated: 'Successful invocations ÷ total invocations, grouped by role. Each row is colour-coded by its own success rate.',
    thresholds: [
      { color: 'emerald', label: '≥ 95% success', meaning: 'Role is reliable.' },
      { color: 'amber', label: '80% – 94%', meaning: 'Intermittent failures. Worth a log review.' },
      { color: 'rose', label: '< 80%', meaning: 'Role is broken or rate-limited. Cohesion will tank.' },
    ],
    recommendations: [
      'Drill into the "Recent Pipeline Errors" tile — failures are listed by role.',
      'If "Intent Classification" is failing: LLM key budget or rate limits. Check Emergent profile.',
      'If "Tool Routing" is failing: ToolRouter contract may have changed. Run `pytest backend/tests/v3/test_coordinator.py`.',
    ],
  },
  workflow: {
    definition: 'The pulse of the platform — how many user queries we\'ve handled, what percentage succeeded, and how fast they came back. The single most important Beta-health metric.',
    howCalculated: 'Counts and percentiles computed from one telemetry row per query (`v3_team_cohesion_event`). P50 = median; P95 = 95th percentile (only 5% of queries are slower).',
    thresholds: [
      { color: 'emerald', label: 'Success ≥ 95% AND P95 < 8 s', meaning: 'Beta health is solid.' },
      { color: 'amber', label: 'Success 80–94% OR P95 8–15 s', meaning: 'Degraded. Worth investigating.' },
      { color: 'rose', label: 'Success < 80% OR P95 > 15 s', meaning: 'Beta UX is broken. Users will churn.' },
    ],
    recommendations: [
      'If success rate dropped: open the Recent Pipeline Errors tile.',
      'If P95 spiked: open the Per-Agent Latency tile to find the slow agent.',
      'Sudden drops to 0 in the hourly histogram = service crash; ramp-ups = Beta adoption.',
    ],
  },
  deletion: {
    definition: 'Privacy compliance signal. When a user asks us to delete their account, we should also wipe everything connected to it (uploads, history, preferences, embeddings). This tile confirms the cascade ran cleanly each time.',
    howCalculated: 'One audit row per deleted resource, written by the Deletion Runbook. Healthy ratio is ~7 child rows per parent (one parent triggers ~7 cascading child wipes).',
    thresholds: [
      { color: 'emerald', label: 'Cascades ≈ 7× parents', meaning: 'Deletion runbook is wiping all child resources cleanly per BRD §6.' },
      { color: 'amber', label: 'Cascades 2× – 6× parents', meaning: 'Some child resources may not be cascading. Audit needed.' },
      { color: 'rose', label: 'Parents but no cascades', meaning: 'Cascade rules misconfigured — orphaned data is accumulating.' },
    ],
    recommendations: [
      'Run `pytest backend/tests/v3/test_deletion_runbook.py` to confirm cascade rules still pass.',
      'Sample-audit: `db.v3_deletion_event.find({"payload.reason":"user_request"}).limit(3)` then verify all 8 child collections were wiped.',
      'If parents > 0 and cascades = 0: a recent change broke the cascade. Check git log for runbook.py edits.',
    ],
  },
  pipelineErrors: {
    definition: 'The 20 most recent agent failures across the whole system. The first place to look when something goes red — every entry shows which agent broke, the error message, when, and which user query triggered it.',
    howCalculated: 'All trace rows where `ok=false`, sorted by recency.',
    thresholds: [
      { color: 'emerald', label: '0 entries OR all > 24h old', meaning: 'No active failure modes.' },
      { color: 'amber', label: 'Entries in last hour, < 5', meaning: 'Sporadic failures. Check error patterns.' },
      { color: 'rose', label: '≥ 5 entries in last hour', meaning: 'Active failure storm. Likely a transient outage or breaking change.' },
    ],
    recommendations: [
      'If errors cluster on one agent: that agent likely has a code/config issue.',
      'If errors cluster on one workflow_id: a single user query triggered the failure — reproduce it via `/api/query/start` with the same query.',
      'For LLM rate-limit errors (429): check budget at Emergent Profile → Universal Key.',
    ],
  },
  userEvents: {
    definition: 'First-party Beta analytics. The primary big number is **Unique Beta Visitors** — distinct logged-in tenants who fired at least one tracked event in the window, with the operator + harness accounts subtracted so investor-facing metrics reflect real user behaviour, not your own QA traffic. Secondary numbers show total tracked clicks and the raw union (includes exempt operator tenants for comparison). No third-party analytics vendor is involved — events live inside our own audit chain and use HMAC tenant_id only (no raw username).',
    howCalculated: 'Row per click in v3_user_event. Aggregated by event_name over the last 7 days. unique_beta_visitors = union of HMAC tenant_ids across all events, MINUS the exempt set (hardcoded EXEMPT_USERNAMES ∪ any beta_users row flagged exempt=true via PUT /admin/beta-users/{u}/exempt). Anonymous / pre-login traffic is always excluded from the union.',
    thresholds: [
      { color: 'emerald', label: '≥ 1 unique Beta visitor', meaning: 'Capture path is live and at least one real Beta user has interacted with the tracked surfaces.' },
      { color: 'slate', label: 'Zero unique Beta visitors', meaning: 'No real (non-operator) Beta clicks captured yet. Expected before public Beta opens.' },
      { color: 'rose', label: 'Endpoint error', meaning: 'The aggregation endpoint returned an error — backend or Mongo issue.' },
    ],
    recommendations: [
      'If unique Beta visitors is 0 but total clicks is high, everything in the window is operator/harness traffic — mark those accounts exempt via PUT /admin/beta-users/{username}/exempt so the tile reads correctly.',
      'High Revenue Share opens + low Demo downloads = users intrigued by the model but not yet wowed by the product. Investigate the demo path.',
      'High Accuracy Control toggle count = users actively exercising data-stewardship — the strongest brand-promise signal in the campaign.',
    ],
  },
  financial: {
    definition: 'Variable LLM spend (Anthropic Claude tokens billed via Emergent LLM Key) plus the fixed monthly cost of running the platform. Powers daily cost monitoring and the cost-profile investor doc. Pricing snapshot: Sonnet $3/$15 per 1M in/out tokens, Haiku $1/$5 per 1M.',
    howCalculated: 'Reads v3_llm_cost_ledger over the rolling window. Per-row: usd = (input_tokens / 1M × input_rate) + (output_tokens / 1M × output_rate) based on the model name. Daily averages and 30-day projection are simple windowed extrapolation — not seasonality-adjusted.',
    thresholds: [
      { color: 'emerald', label: '$0 < spend ≤ budget', meaning: 'Active LLM tracking with healthy burn rate.' },
      { color: 'amber', label: 'Ledger empty', meaning: 'No LLM cost rows yet. Either no traffic or record_llm_usage is not wired into LlmChat (backlog item).' },
      { color: 'rose', label: 'Endpoint error', meaning: 'Aggregation failed.' },
    ],
    recommendations: [
      'Wire record_llm_usage into v3.integrations.LlmChat so every call writes a ledger row.',
      'Top up the Emergent LLM Key from Profile → Universal Key before campaign launches if the 30-day projection trends toward your budget cap.',
      'Watch cost-per-query — if it climbs above $0.05, audit prompt sizes (likely a Sonnet call doing work a Haiku call could handle).',
    ],
  },
  releases: {
    definition: 'Quality checks tied to each new platform release. Before we ship a v3.x version, we run a series of gates (cohesion threshold, accuracy threshold, error budget, etc) and only release if they all pass. Empty until you begin formal release tagging.',
    howCalculated: 'One row per gate evaluation. Pass/fail counts grouped by release tag.',
    thresholds: [
      { color: 'emerald', label: 'Most recent release: all gates passed', meaning: 'Release was production-safe.' },
      { color: 'amber', label: 'Some gates failed but < 3 total', meaning: 'Acceptable rollout with known issues.' },
      { color: 'rose', label: '≥ 3 gates failed in latest release', meaning: 'Release should be rolled back.' },
    ],
    recommendations: [
      'When you cut a v3.x release, write rows via `write_gate_result(db, release_id="v3.1.0", gate_name="cohesion>=0.8", passed=True)`.',
      'If empty: that\'s expected for Beta. Release gates are a Phase 6 deliverable.',
    ],
  },
  accuracy: {
    definition: 'How accurate the AI-generated answers actually are — the metric users care about most. Combines three independent signals: did users like it (subjective), did the drafter cite its sources (objective evidence), and did our internal grounding check pass (objective verification). v0 today: feedback-only when no drafted answers exist yet.',
    howCalculated: 'Weighted blend, only using components that have data: 50% × user feedback ratio + 30% × citation coverage (% of answers with ≥2 [[n]] citations) + 20% × verification pass rate (% of answers that are non-empty AND well-cited). Weights renormalise if a component has no data yet.',
    thresholds: [
      { color: 'emerald', label: '≥ 0.80', meaning: 'Answers are accurate, well-cited, and well-received.' },
      { color: 'amber', label: '0.60 – 0.79', meaning: 'Acceptable but inconsistent. One signal is dragging — check the components below.' },
      { color: 'rose', label: '< 0.60', meaning: 'Quality issue. Could be the prompt, the retrieval, or the user expectation. Investigate.' },
    ],
    recommendations: [
      'Look at the three components below the score — the lowest one tells you what to fix first.',
      'Low citation coverage → the drafter LLM is skipping the [[n]] format. Re-check the system prompt.',
      'Low verification pass rate → answers are too short or unsupported. May need RAG context tuning.',
      'Low user feedback ratio with high citations → users are dissatisfied for a non-accuracy reason (length, tone, format).',
    ],
  },
  clarifications: {
    definition: 'How often the system asks the user a clarifying question before answering an ambiguous query. Triggered when intent confidence is low or the query is multi-meaning. Properly used, this UPLIFTS Result Accuracy and User Feedback because we answer what the user actually meant — not our best guess.',
    howCalculated: '"Triggered" = Disambiguation Agent ran for this query. "Asked" = the agent decided to surface a clarifying question to the user. Clarification rate = triggered ÷ total queries. Ask rate = asked ÷ triggered.',
    thresholds: [
      { color: 'emerald', label: '5% – 15% clarification rate', meaning: 'Healthy. Catching genuine ambiguity without over-asking.' },
      { color: 'amber', label: '< 5% OR 15% – 30%', meaning: 'Either we\'re not catching ambiguity (under-asking) or we\'re bothering users too much (over-asking).' },
      { color: 'rose', label: '> 30%', meaning: 'Over-asking — users will abandon.' },
    ],
    recommendations: [
      'Compare User Feedback ratio for clarified vs unclarified queries (next-session enhancement) — clarifications should LIFT feedback, not drag it.',
      'If clarification rate is 0%: the disambiguation gate may not be wired into the production pipeline.',
      'If clarifications are happening but users aren\'t engaging with them: the clarifying question UI needs work.',
    ],
  },
  confidence: {
    definition: 'How sure the Intent Agent is about what the user meant, averaged across the window. This is the LEADING indicator — if confidence drops, clarifications should rise and accuracy should eventually fall. Watching this first lets us act before users complain.',
    howCalculated: 'Mean of IntentAgent.confidence (0.0 – 1.0) across every query in the window. Thresholds track Disambiguation Function rules: <0.65 always triggers a clarifying question; <0.85 triggers on action queries (high-risk mistakes).',
    thresholds: [
      { color: 'emerald', label: '≥ 0.85 mean confidence', meaning: 'The Intent Agent is confident on most queries — system is grounded.' },
      { color: 'amber', label: '0.70 – 0.84', meaning: 'Many queries sitting near the disambiguation threshold. Expect rising clarifications.' },
      { color: 'rose', label: '< 0.70', meaning: 'Intent Agent is often unsure. Tune its prompt or expand intent taxonomy.' },
    ],
    recommendations: [
      'If confidence drops and clarifications DON\'T rise → disambiguation threshold is too loose.',
      'If the <0.65 bucket is > 10% → user queries are shifting — review recent query logs.',
      'Leading indicator: move on this BEFORE accuracy craters.',
    ],
  },
  efficacy: {
    definition: 'Did the clarifying question actually help? We match each clarification ask to the same user\'s NEXT query within 15 minutes and measure whether confidence rose, the user reformulated, or they abandoned. Answers "are we asking questions that clarify root intent?"',
    howCalculated: 'For each "needs_clarification=True" disambiguation: find same-user next workflow within 15 min. Bucket: helped (new conf ≥ old + 0.15) · user_clarified (intent type changed — they reformulated) · unhelpful (same/lower conf) · abandoned (no follow-up). Efficacy = (helped + user_clarified) / total.',
    thresholds: [
      { color: 'emerald', label: '≥ 60% efficacy', meaning: 'Clarifications are pulling their weight — keep the threshold.' },
      { color: 'amber', label: '30% – 59%', meaning: 'Mixed. Abandonment is likely high — check the clarifying question copy.' },
      { color: 'rose', label: '< 30%', meaning: 'Clarifications are hurting more than helping. Either questions are poor or users don\'t trust them.' },
    ],
    recommendations: [
      'High "abandoned" → the question is confusing or intrusive. Rewrite to be shorter / more specific.',
      'High "unhelpful" (stuck at same confidence) → we\'re asking, user answers, we still can\'t classify. Intent taxonomy gap.',
      'High "user_clarified" → good sign — users are willing to reformulate. The question opened the door even if it didn\'t close it.',
    ],
  },
  correlation: {
    definition: 'Daily time-series of the 3 linked signals — Mean Confidence, Clarification Rate, User Feedback Ratio — overlaid so the team can see the causal chain in one view. Confidence leads, clarifications mediate, feedback lags. Breaks in the chain expose threshold issues.',
    howCalculated: 'Day buckets over the window. Per-day mean confidence from intent traces · per-day clarification rate from disambiguation traces ÷ workflows · per-day feedback ratio from answer_feedback. N in each day printed alongside so you know when to trust a spike.',
    thresholds: [
      { color: 'emerald', label: 'Confidence trending up OR stable, feedback stable/up', meaning: 'System is improving or stable.' },
      { color: 'amber', label: 'One line is trending against the others', meaning: 'Investigate the disconnect — threshold or pipeline issue.' },
      { color: 'rose', label: 'Confidence dropping AND feedback dropping AND clarifications flat', meaning: 'We\'re not catching the degradation — disambiguation threshold is wrong.' },
    ],
    recommendations: [
      'Expect these to correlate. If they decouple, the pipeline has a leak.',
      'Clarifications should RISE when confidence FALLS — that\'s the gate working.',
      'Feedback should RISE when clarifications are efficacious — link with the Efficacy tile.',
    ],
  },
};

const InfoPanel = ({ info, onClose, testIdRoot }) => (
  <div
    className="mt-3 p-4 bg-slate-950/80 border border-slate-800 rounded-lg text-xs leading-relaxed"
    data-testid={`${testIdRoot}-info-panel`}
  >
    <div className="flex justify-between items-start mb-3">
      <span className="text-cyan-400 font-semibold uppercase tracking-wider text-[10px]">
        What this measures
      </span>
      <button
        onClick={onClose}
        className="text-slate-500 hover:text-slate-300"
        data-testid={`${testIdRoot}-info-close`}
      >
        <X size={14} />
      </button>
    </div>
    <p className="text-slate-300 mb-3">{info.definition}</p>

    <div className="text-cyan-400 font-semibold uppercase tracking-wider text-[10px] mb-2">
      How it&apos;s calculated
    </div>
    <p className="text-slate-300 mb-3">{info.howCalculated}</p>

    <div className="text-cyan-400 font-semibold uppercase tracking-wider text-[10px] mb-2">
      Traffic-light thresholds
    </div>
    <ul className="space-y-1.5 mb-3">
      {info.thresholds.map((t) => (
        <li key={t.label} className="flex items-start gap-2">
          <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${LIGHT_DOT[t.color]}`} />
          <span>
            <span className={`font-semibold ${LIGHT_TEXT[t.color]}`}>{t.label}</span>
            <span className="text-slate-400"> — {t.meaning}</span>
          </span>
        </li>
      ))}
    </ul>

    <div className="text-cyan-400 font-semibold uppercase tracking-wider text-[10px] mb-2">
      If this is yellow / red
    </div>
    <ol className="space-y-1 list-decimal list-inside text-slate-300">
      {info.recommendations.map((r, i) => <li key={i}>{r}</li>)}
    </ol>
  </div>
);

const Tile = ({ icon: Icon, title, status, children, testId, infoKey, openInfoKey, setOpenInfoKey }) => {
  const isOpen = openInfoKey === infoKey;
  const info = METRIC_INFO[infoKey];
  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 backdrop-blur-sm" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-slate-300">
          <Icon size={18} className="text-cyan-400" />
          <h3 className="text-sm font-medium tracking-wide uppercase">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {status && (
            <span className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${LIGHT_DOT[status.color]}`} />
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${LIGHT_TEXT[status.color]}`}>
                {status.label}
              </span>
            </span>
          )}
          {info && (
            <button
              onClick={() => setOpenInfoKey(isOpen ? null : infoKey)}
              className="text-slate-500 hover:text-cyan-400 transition-colors"
              title="What does this mean?"
              data-testid={`${testId}-info-btn`}
            >
              <Info size={14} />
            </button>
          )}
        </div>
      </div>
      {children}
      {isOpen && info && (
        <InfoPanel info={info} testIdRoot={testId} onClose={() => setOpenInfoKey(null)} />
      )}
    </div>
  );
};

const Stat = ({ label, value, hint, valueClass = 'text-slate-100' }) => (
  <div className="flex items-baseline justify-between gap-3 py-1">
    <span className="text-xs text-slate-400">{label}</span>
    <span className={`text-base font-semibold tabular-nums ${valueClass}`}>
      {value}
      {hint && <span className="text-xs text-slate-500 font-normal ml-1.5">{hint}</span>}
    </span>
  </div>
);

const fetchJson = async (path, token) => {
  const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json();
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openInfoKey, setOpenInfoKey] = useState(null);

  // SPD v5.91 (2026-02-16) — inline exempt-flag management for the Beta User
  // Events tile. Opens on demand so the beta_users list is never fetched
  // just to render the dashboard. Toggle writes call PUT /admin/beta-users/
  // {u}/exempt and re-fire `refresh()` so the tile's unique-visitor count
  // updates on the same round-trip.
  const [exemptOpen, setExemptOpen] = useState(false);
  const [betaUsers, setBetaUsers] = useState(null); // null = not loaded yet
  const [betaUsersErr, setBetaUsersErr] = useState(null);
  const [mutatingExempt, setMutatingExempt] = useState({}); // {username: true}
  // IJ-259 (2026-08-05) — inline demo-admin flag management. Mirrors the
  // exempt toggle above. Flip on before a client demo · flip off after.
  // Grants the beta user access to /knowledge-controls ONLY · does not
  // unlock any other admin surface.
  const [mutatingDemoAdmin, setMutatingDemoAdmin] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('startech_token');
    if (!token) { navigate('/'); return; }
    const claims = decodeJwt(token);
    if (!claims || claims.sub !== 'startech') navigate('/');
  }, [navigate]);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('startech_token');
    if (!token) return;
    setRefreshing(true);
    setError(null);
    try {
      const [
        cohesion, permissions, retrieval, traces, feedback,
        workflow, deletion, errors, releases, accuracy, clarifications,
        confidence, efficacy, correlation, userEvents, financial, security,
        securityHealth, exitBanner, vaultSecurity,
      ] = await Promise.all([
        fetchJson('/api/admin/cohesion/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/permissions/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/retrieval/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/traces/summary', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/feedback/summary', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/workflow/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/deletion/summary?days=30', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/pipeline/errors?limit=20', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/release-gate/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/accuracy/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/clarifications/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/confidence/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/clarifications/efficacy?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/correlation/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/user-events/summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/v1/admin/financial-summary?days=7', token).catch((e) => ({ _err: e.message })),
        fetchJson('/api/admin/security/snapshots', token).catch((e) => ({ _err: e.message })),
        // SPD v5.72 (2026-02-11) — Security Health tile · read-only
        // aggregation of existing v3/security/* signals. Refreshes on
        // the same 30s poll as every other tile.
        fetchJson('/api/admin/security-health', token).catch((e) => ({ _err: e.message })),
        // 2026-02-13 · operator-approved exit-banner analytics tile.
        // Feeds off the new /api/exit-banner/dismissed events written
        // whenever a real (non-exempt) user clicks [Got it] on the
        // Session Exit Banner.
        fetchJson('/api/admin/exit-banner/analytics', token).catch((e) => ({ _err: e.message })),
        // IJ-272 (2026-08-12) — operator-only Secure Vault Upload rejection banner.
        // Any row here means a non-operator caller hit POST /api/admin/docs/upload.
        // Rendered as a red banner just below the header when count > 0.
        fetchJson('/api/admin/docs/security-events?days=7', token).catch((e) => ({ _err: e.message })),
      ]);
      setData({
        cohesion, permissions, retrieval, traces, feedback,
        workflow, deletion, errors, releases, accuracy, clarifications,
        confidence, efficacy, correlation, userEvents, financial, security,
        securityHealth, exitBanner, vaultSecurity,
      });
      setLastRefresh(new Date());
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [refresh]);

  // ─── SPD v5.91 · beta_users list + exempt toggle ───────────────────────
  const loadBetaUsers = useCallback(async () => {
    const token = localStorage.getItem('startech_token');
    if (!token) return;
    setBetaUsersErr(null);
    try {
      const res = await fetch(`${API}/api/admin/beta-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`beta-users → ${res.status}`);
      const j = await res.json();
      setBetaUsers(Array.isArray(j.users) ? j.users : []);
    } catch (e) {
      setBetaUsersErr(String(e.message || e));
    }
  }, []);

  const toggleExempt = useCallback(async (username, nextExempt) => {
    const token = localStorage.getItem('startech_token');
    if (!token) return;
    setMutatingExempt((m) => ({ ...m, [username]: true }));
    try {
      const res = await fetch(
        `${API}/api/admin/beta-users/${encodeURIComponent(username)}/exempt`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ exempt: nextExempt }),
        },
      );
      if (!res.ok) throw new Error(`exempt toggle → ${res.status}`);
      // Optimistic local update + summary refresh so the tile count
      // reflects the change on the same click.
      setBetaUsers((prev) =>
        (prev || []).map((u) =>
          u.username === username ? { ...u, exempt: nextExempt } : u,
        ),
      );
      refresh();
    } catch (e) {
      setBetaUsersErr(String(e.message || e));
    } finally {
      setMutatingExempt((m) => {
        const next = { ...m };
        delete next[username];
        return next;
      });
    }
  }, [refresh]);

  // IJ-259 (2026-08-05) — Demo-admin toggle. Mirrors toggleExempt above.
  // Calls PUT /admin/beta-users/{u}/demo-admin so the beta user can access
  // the Knowledge Control Cockpit route without unlocking the admin
  // dashboard · admin agents · admin scenarios · admin code maintenance ·
  // or governance tile. Manual revoke path for v1.
  const toggleDemoAdmin = useCallback(async (username, nextDemoAdmin) => {
    const token = localStorage.getItem('startech_token');
    if (!token) return;
    setMutatingDemoAdmin((m) => ({ ...m, [username]: true }));
    try {
      const res = await fetch(
        `${API}/api/admin/beta-users/${encodeURIComponent(username)}/demo-admin`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ is_demo_admin: nextDemoAdmin }),
        },
      );
      if (!res.ok) throw new Error(`demo-admin toggle → ${res.status}`);
      setBetaUsers((prev) =>
        (prev || []).map((u) =>
          u.username === username ? { ...u, is_demo_admin: nextDemoAdmin } : u,
        ),
      );
    } catch (e) {
      setBetaUsersErr(String(e.message || e));
    } finally {
      setMutatingDemoAdmin((m) => {
        const next = { ...m };
        delete next[username];
        return next;
      });
    }
  }, []);

  // SPD v6.04 · IJ-096 — Beta lockout unlock. Called from the inline
  // "Unlock" button that renders next to a user row when
  // ``u.is_locked`` is true. Deletes the row from
  // ``beta_user_lockouts`` and refreshes the list so the pill and
  // button disappear on the same click.
  const [mutatingUnlock, setMutatingUnlock] = useState({});
  const unlockBetaUser = useCallback(async (username) => {
    const token = localStorage.getItem('startech_token');
    if (!token) return;
    setMutatingUnlock((m) => ({ ...m, [username]: true }));
    try {
      const res = await fetch(
        `${API}/api/admin/beta-users/${encodeURIComponent(username)}/unlock`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!res.ok) throw new Error(`unlock → ${res.status}`);
      loadBetaUsers();
    } catch (e) {
      setBetaUsersErr(String(e.message || e));
    } finally {
      setMutatingUnlock((m) => {
        const next = { ...m };
        delete next[username];
        return next;
      });
    }
  }, [loadBetaUsers]);

  // Lazily load beta users the first time the operator opens the panel.
  useEffect(() => {
    if (exemptOpen && betaUsers === null) loadBetaUsers();
  }, [exemptOpen, betaUsers, loadBetaUsers]);

  const cohesion = data.cohesion || {};
  const permissions = data.permissions || {};
  const retrieval = data.retrieval || {};
  const traces = data.traces || {};
  const feedback = data.feedback || {};
  const workflow = data.workflow || {};
  const deletion = data.deletion || {};
  const errors = data.errors || {};
  const releases = data.releases || {};
  const accuracy = data.accuracy || {};
  const clarifications = data.clarifications || {};
  const confidence = data.confidence || {};
  const efficacy = data.efficacy || {};
  const correlation = data.correlation || {};
  const userEvents = data.userEvents || {};
  const financial = data.financial || {};
  const security = data.security || {};
  const securityHealth = data.securityHealth || {};
  const exitBanner = data.exitBanner || {};

  // Traffic-light status per metric
  const cohesionStatus = evalLight(cohesion.per_workflow_avg_cohesion, { greenAt: 0.8, yellowAt: 0.5, direction: 'up' });
  const permissionsStatus = evalLight(permissions.block_rate, { greenAt: 0.2, yellowAt: 0.5, direction: 'down' });
  const retrievalStatus = (() => {
    if (retrieval._err) return { color: 'rose', label: 'Error' };
    const cnt = retrieval.vector_embedding_count ?? 0;
    const routes = Object.keys(retrieval.tool_route_distribution || {}).length;
    if (cnt > 0 && routes >= 2) return { color: 'emerald', label: 'Healthy' };
    if (cnt > 0 || routes > 0) return { color: 'amber', label: 'Watch' };
    return { color: 'rose', label: 'At risk' };
  })();
  // User feedback health — use 7d window so it aligns with the Accuracy tile
  // (which also uses 7d) and with the Status badge denominator.
  const feedbackStatus = (() => {
    const w = feedback.last_7d || {};
    const total = (w.up || 0) + (w.down || 0);
    if (!total) return { color: 'slate', label: 'No data' };
    return evalLight((w.up || 0) / total, { greenAt: 0.8, yellowAt: 0.6, direction: 'up' });
  })();
  // Per-agent latency — exclude the pipeline orchestrator kind (which by
  // definition sums all sub-agent latencies and would always dominate max).
  const latencyStatus = (() => {
    const rows = (traces.per_agent || []).filter(
      (r) => (r.agent_kind || '') !== 'pipeline'
    );
    if (!rows.length) return { color: 'slate', label: 'No data' };
    const maxAvg = Math.max(...rows.map((r) => r.avg_latency_ms || 0));
    return evalLight(maxAvg, { greenAt: 2000, yellowAt: 5000, direction: 'down' });
  })();
  const agentKindStatus = (() => {
    const rows = cohesion.per_agent_kind || [];
    if (!rows.length) return { color: 'slate', label: 'No data' };
    const minRate = Math.min(...rows.map((r) => r.success_rate ?? 1));
    return evalLight(minRate, { greenAt: 0.95, yellowAt: 0.8, direction: 'up' });
  })();
  // Piece 1 — leading-indicator health of query confidence
  const confidenceStatus = (() => {
    if (confidence._err || confidence.mean_confidence == null) return { color: 'slate', label: 'No data' };
    return evalLight(confidence.mean_confidence, { greenAt: 0.85, yellowAt: 0.7, direction: 'up' });
  })();
  // Piece 3 — did the clarifying question actually help?
  const efficacyStatus = (() => {
    if (efficacy._err || efficacy.efficacy_rate == null) return { color: 'slate', label: 'No data' };
    return evalLight(efficacy.efficacy_rate, { greenAt: 0.6, yellowAt: 0.3, direction: 'up' });
  })();
  const workflowStatus = (() => {
    if (workflow._err || workflow.total_workflows == null) return { color: 'slate', label: 'No data' };
    const sr = workflow.success_rate ?? 0;
    const p95 = workflow.p95_latency_ms ?? 0;
    if (sr >= 0.95 && p95 < 8000) return { color: 'emerald', label: 'Healthy' };
    if (sr >= 0.8 && p95 < 15000) return { color: 'amber', label: 'Watch' };
    return { color: 'rose', label: 'At risk' };
  })();
  const deletionStatus = (() => {
    if (deletion._err) return { color: 'rose', label: 'Error' };
    if (!deletion.total_deletions) return { color: 'slate', label: 'No data' };
    const byType = deletion.by_subject_type || [];
    const parents = byType.find((r) => r.subject_type === 'user_account');
    if (!parents) {
      // No user_account deletions in window — only direct cleanup activity.
      // That's fine operationally; just nothing to assess cascade-wise.
      return { color: 'slate', label: 'No user deletions' };
    }
    const cascadeCount = byType.reduce((acc, r) => acc + (r.cascades || 0), 0);
    if (parents.count > 0 && cascadeCount === 0) {
      // Parent deletes happened but NO cascade audit rows fired —
      // either users had no data, or the runbook silently failed.
      return { color: 'amber', label: 'No cascades' };
    }
    // Any cascade activity = healthy (the runbook is firing).
    // Deletion completeness is enforced by the release gate, not this tile.
    return { color: 'emerald', label: 'Healthy' };
  })();
  const userEventsStatus = (() => {
    if (userEvents._err) return { color: 'rose', label: 'Error' };
    // SPD v5.91 — status now keys off the primary displayed number
    // (unique Beta visitors) so the tile colour matches the headline
    // stat. Falls back to total_events for backward compatibility with
    // older API responses that predate the exempt-filter field.
    const primary =
      userEvents.unique_beta_visitors ?? userEvents.total_events ?? 0;
    if (primary === 0) return { color: 'slate', label: 'No visitors' };
    return { color: 'emerald', label: 'Healthy' };
  })();
  const errorsStatus = (() => {
    if (errors._err) return { color: 'rose', label: 'Error' };
    // Prefer the server-computed recent_count (timezone-correct).
    // Fall back to client-side filter for older API responses.
    let recentCount = errors.recent_count;
    if (typeof recentCount !== 'number') {
      const list = errors.errors || [];
      recentCount = list.filter((e) => {
        try { return (Date.now() - new Date(e.created_at).getTime()) < 60 * 60 * 1000; }
        catch (_) { return false; }
      }).length;
    }
    if (!recentCount) return { color: 'emerald', label: 'Healthy' };
    if (recentCount < 5) return { color: 'amber', label: 'Watch' };
    return { color: 'rose', label: 'At risk' };
  })();
  const releasesStatus = (() => {
    if (releases._err) return { color: 'rose', label: 'Error' };
    const summary = releases.summary;
    if (!summary || typeof summary.passing !== 'number') return { color: 'slate', label: 'No data' };
    if (summary.failing === 0 && summary.passing > 0) return { color: 'emerald', label: 'Healthy' };
    if (summary.failing <= 1) return { color: 'amber', label: 'Watch' };
    return { color: 'rose', label: 'At risk' };
  })();
  const accuracyStatus = evalLight(accuracy.result_accuracy_score, { greenAt: 0.8, yellowAt: 0.6, direction: 'up' });
  const clarificationsStatus = (() => {
    if (clarifications._err) return { color: 'rose', label: 'Error' };
    const r = clarifications.clarification_rate;
    const total = clarifications.total_workflows ?? 0;
    if (r == null) return { color: 'slate', label: 'No data' };
    // Below 100-workflow sample, don't flag red — statistical noise dominates.
    // Insight is delivered via the absolute number on the tile face.
    if (total < 100) {
      if (r >= 0.05 && r <= 0.30) return { color: 'emerald', label: 'Healthy' };
      return { color: 'amber', label: 'Watch (low sample)' };
    }
    if (r >= 0.05 && r <= 0.15) return { color: 'emerald', label: 'Healthy' };
    if (r > 0.30) return { color: 'rose', label: 'At risk' };
    return { color: 'amber', label: 'Watch' };
  })();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-slate-200 p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-cyan-400 tracking-tight" data-testid="admin-dashboard-title">
              Search.Inc · Admin Observability
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Live QC flywheel · 7-day rolling window · auto-refresh every 30s
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefresh && (
              <span className="text-xs text-slate-500" data-testid="admin-last-refresh">
                Last refreshed: {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm border border-slate-700 disabled:opacity-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCcw size={14} className={refreshing ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm border border-slate-700"
              data-testid="admin-back-btn"
            >
              Back to app
            </button>
          </div>
        </div>

        {/* Purpose / Overview banner */}
        <div className="mb-8 p-5 rounded-xl border border-cyan-900/40 bg-gradient-to-r from-slate-900/80 to-cyan-950/20" data-testid="admin-overview">
          <div className="flex items-start gap-3">
            <Database size={18} className="text-cyan-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm leading-relaxed text-slate-300 flex-1">
              <p className="font-semibold text-slate-100 mb-1">Why this dashboard exists</p>
              <p className="mb-2">
                Search.Inc runs a team of <span className="text-cyan-400 font-semibold">18 specialized agents</span>
                {' '}(Coordinator → Intent → Permissions → Retrieval → Drafting → Verification, plus 12 supporting roles per the v3.2 Agent BRD).
                Every Beta query writes telemetry into 4 v3 collections so we can grade agent performance the moment users complain
                — instead of hunting through logs after the fact. This is your <span className="text-cyan-400 font-medium">QC flywheel</span>.
              </p>
              <p className="text-slate-400 text-xs">
                Each tile shows one signal · the coloured dot reads <span className="text-emerald-400 font-semibold">green</span> /
                <span className="text-amber-400 font-semibold"> yellow</span> /
                <span className="text-rose-400 font-semibold"> red</span> at a glance · click the
                <Info size={12} className="inline mx-1 align-text-bottom" />
                icon on any tile for definition, calculation, thresholds and recommended fixes.
                Data refreshes every 30s; nothing is cached client-side.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                <button
                  onClick={() => navigate('/admin/agents')}
                  className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 font-semibold underline-offset-4 hover:underline"
                  data-testid="view-agent-catalog-link"
                >
                  View the full Agent Catalog → name, role and area of expertise for all 18 agents
                </button>
                <button
                  onClick={() => navigate('/admin/scenarios')}
                  className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 font-semibold underline-offset-4 hover:underline"
                  data-testid="view-scenario-library-link"
                >
                  Open the Scenario Library Runner → manual fine-tuning + uplift vs baseline (540 scenarios)
                </button>
                <button
                  onClick={() => navigate('/admin/code-maintenance')}
                  className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 font-semibold underline-offset-4 hover:underline"
                  data-testid="view-code-maintenance-link"
                >
                  Open Code Maintenance → refactor phase assessments &amp; evidence reports
                </button>
                <button
                  onClick={() => navigate('/admin/ebta')}
                  className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 font-semibold underline-offset-4 hover:underline"
                  data-testid="view-ebta-link"
                >
                  Open the Emergent Build Team Atlas → Errors and Corrections map plus the Atlas Map
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* IJ-272 · Secure Vault rejection banner · operator-only surface */}
        {data.vaultSecurity?.count > 0 && (
          <div
            className="mb-6 p-4 rounded-lg border border-rose-500/70 bg-rose-950/50 text-rose-200"
            data-testid="vault-security-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-rose-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-rose-100">
                  Unauthorised vault upload attempts detected · {data.vaultSecurity.count} in the last 7 days
                </p>
                <p className="text-xs text-rose-300 mt-1">
                  Someone tried to POST to the Secure Vault upload endpoint without operator credentials. Review the list below and rotate the affected account&apos;s token if the actor looks unfamiliar.
                </p>
                <details className="mt-3" data-testid="vault-security-events-details">
                  <summary className="cursor-pointer text-xs text-rose-200 hover:text-rose-100">
                    Show all {data.vaultSecurity.count} attempt{data.vaultSecurity.count === 1 ? '' : 's'}
                  </summary>
                  <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-rose-900/50">
                    <table className="w-full text-[11px]">
                      <thead className="bg-rose-900/40 text-rose-200 uppercase text-[10px]">
                        <tr>
                          <th className="text-left py-1.5 px-2">When (UTC)</th>
                          <th className="text-left py-1.5 px-2">Actor</th>
                          <th className="text-left py-1.5 px-2">Role</th>
                          <th className="text-left py-1.5 px-2">Filename</th>
                          <th className="text-left py-1.5 px-2">IP</th>
                          <th className="text-left py-1.5 px-2">User agent</th>
                        </tr>
                      </thead>
                      <tbody className="text-rose-100">
                        {(data.vaultSecurity.events || []).map((ev, idx) => (
                          <tr key={idx} className="border-t border-rose-900/40" data-testid={`vault-security-row-${idx}`}>
                            <td className="py-1.5 px-2 font-mono text-[10px]">{ev.at}</td>
                            <td className="py-1.5 px-2">{ev.actor || '—'}</td>
                            <td className="py-1.5 px-2">{ev.actor_role || '—'}</td>
                            <td className="py-1.5 px-2 font-mono text-[10px]">{ev.attempted_filename || '—'}</td>
                            <td className="py-1.5 px-2 font-mono text-[10px]">{ev.client_ip || '—'}</td>
                            <td className="py-1.5 px-2 text-[10px] truncate max-w-xs" title={ev.user_agent}>{ev.user_agent || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-rose-950/40 border border-rose-900 rounded-lg text-rose-300 text-sm flex items-center gap-2" data-testid="admin-error">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

          {/* Headline tile — Result Accuracy Score */}
          <Tile
            icon={ShieldCheck} title="Result Accuracy Score" testId="tile-accuracy"
            status={accuracyStatus} infoKey="accuracy"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {accuracy._err ? (
              <p className="text-xs text-rose-400">{accuracy._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[accuracyStatus.color]}`} data-testid="accuracy-score">
                  {accuracy.result_accuracy_score == null ? '—' : accuracy.result_accuracy_score.toFixed(3)}
                </div>
                <p className="text-xs text-slate-500 mb-3">Blended quality score</p>
                <Stat
                  label="User feedback"
                  value={accuracy.components?.user_feedback_ratio == null ? '—' : `${(accuracy.components.user_feedback_ratio * 100).toFixed(1)}%`}
                  hint="weight 50%"
                />
                <Stat
                  label="Citation coverage"
                  value={accuracy.components?.citation_coverage == null ? '—' : `${(accuracy.components.citation_coverage * 100).toFixed(1)}%`}
                  hint="weight 30%"
                />
                <Stat
                  label="Verification pass"
                  value={accuracy.components?.verification_pass_rate == null ? '—' : `${(accuracy.components.verification_pass_rate * 100).toFixed(1)}%`}
                  hint="weight 20%"
                />
                {/* Piece 2 — Root-cause decomposition on 👎 feedback.
                    Converts accuracy from "single number" to "routable signal".
                    Shown only if any 👎 exists in the window. */}
                {(() => {
                  const brc = accuracy.thumbs_down_by_root_cause || {};
                  const total = (brc.intent_misread || 0) + (brc.user_drift || 0)
                              + (brc.engine_quality || 0) + (brc.other || 0)
                              + (brc.unattributed || 0);
                  if (!total) return null;
                  const bar = (label, count, color) => {
                    const pct = total ? Math.round((count / total) * 100) : 0;
                    return (
                      <div className="flex items-center gap-2 text-[11px] mb-1" key={label} data-testid={`root-cause-bar-${label}`}>
                        <span className="w-28 text-slate-400 truncate">{label}</span>
                        <div className="flex-1 h-1.5 bg-slate-800 rounded overflow-hidden">
                          <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-10 text-right text-slate-300 tabular-nums">{count}</span>
                      </div>
                    );
                  };
                  return (
                    <div className="mt-4 pt-3 border-t border-slate-800" data-testid="accuracy-root-cause-block">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">
                        👎 by root cause ({total} total)
                      </p>
                      {bar('Intent misread',  brc.intent_misread  || 0, 'bg-rose-500')}
                      {bar('User drift',      brc.user_drift      || 0, 'bg-amber-500')}
                      {bar('Engine quality',  brc.engine_quality  || 0, 'bg-cyan-500')}
                      {bar('Other',           brc.other           || 0, 'bg-slate-500')}
                      {(brc.unattributed || 0) > 0 && bar('Unattributed', brc.unattributed, 'bg-slate-700')}
                    </div>
                  );
                })()}
              </>
            )}
          </Tile>

          <Tile
            icon={ThumbsUp} title="User Feedback" testId="tile-feedback"
            status={feedbackStatus} infoKey="feedback"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {feedback._err ? (
              <p className="text-xs text-rose-400">{feedback._err}</p>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-1">
                  <div className="flex items-center gap-1.5 text-emerald-400" data-testid="feedback-up">
                    <ThumbsUp size={18} />
                    <span className="text-2xl font-bold tabular-nums">{feedback.last_7d?.up ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-rose-400" data-testid="feedback-down">
                    <ThumbsDown size={18} />
                    <span className="text-2xl font-bold tabular-nums">{feedback.last_7d?.down ?? 0}</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-3">Last 7 days</p>
                <Stat label="All-time total" value={feedback.total ?? 0} />
                <Stat label="All-time up / down" value={`${feedback.up ?? 0}↑ / ${feedback.down ?? 0}↓`} />
                <Stat label="Negative w/ comments (all-time)" value={feedback.down_with_comments ?? 0} />
              </>
            )}
          </Tile>

          <Tile
            icon={MessageSquare} title="Query Clarifications" testId="tile-clarifications"
            status={clarificationsStatus} infoKey="clarifications"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {clarifications._err ? (
              <p className="text-xs text-rose-400">{clarifications._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[clarificationsStatus.color]}`} data-testid="clarifications-rate">
                  {clarifications.clarification_rate == null ? '—' : `${(clarifications.clarification_rate * 100).toFixed(1)}%`}
                </div>
                <p className="text-xs text-slate-500 mb-3">Of queries trigger clarification</p>
                <Stat label="Total queries" value={clarifications.total_workflows ?? 0} />
                <Stat label="Disambiguation runs" value={clarifications.disambiguation_runs ?? 0} />
                <Stat label="Asked the user" value={clarifications.clarifications_asked ?? 0} />
                <Stat label="Skipped (high conf.)" value={clarifications.clarifications_skipped_low_confidence ?? 0} />
                <Stat
                  label="Ask rate when triggered"
                  value={clarifications.ask_rate_when_triggered == null ? '—' : `${(clarifications.ask_rate_when_triggered * 100).toFixed(1)}%`}
                />
              </>
            )}
          </Tile>

          {/* Piece 1 — Query Confidence Score (leading indicator) */}
          <Tile
            icon={Target} title="Query Confidence" testId="tile-confidence"
            status={confidenceStatus} infoKey="confidence"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {confidence._err ? (
              <p className="text-xs text-rose-400">{confidence._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[confidenceStatus.color]}`} data-testid="confidence-mean">
                  {confidence.mean_confidence == null ? '—' : confidence.mean_confidence.toFixed(3)}
                </div>
                <p className="text-xs text-slate-500 mb-3">Mean intent confidence (leading indicator)</p>
                <Stat label="Intent runs" value={confidence.total_intent_runs ?? 0} />
                <Stat
                  label="Below 0.65 (always ask)"
                  value={confidence.pct_below_065 == null ? '—' : `${(confidence.pct_below_065 * 100).toFixed(1)}%`}
                />
                <Stat
                  label="Below 0.85 (action ask)"
                  value={confidence.pct_below_085 == null ? '—' : `${(confidence.pct_below_085 * 100).toFixed(1)}%`}
                />
                <Stat
                  label="High confidence (≥ 0.85)"
                  value={confidence.pct_high_confidence == null ? '—' : `${(confidence.pct_high_confidence * 100).toFixed(1)}%`}
                />
                {/* Mini distribution sparkbar */}
                {(() => {
                  const b = confidence.distribution_buckets || {};
                  const arr = [b.lt_0_5 || 0, b['0_5_to_0_65'] || 0, b['0_65_to_0_8'] || 0, b['0_8_to_0_9'] || 0, b.gte_0_9 || 0];
                  const max = Math.max(1, ...arr);
                  const labels = ['<0.5', '0.5-0.65', '0.65-0.8', '0.8-0.9', '≥0.9'];
                  const colors = ['bg-rose-500', 'bg-rose-400', 'bg-amber-500', 'bg-emerald-400', 'bg-emerald-500'];
                  return (
                    <div className="mt-4 pt-3 border-t border-slate-800" data-testid="confidence-distribution">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Distribution</p>
                      <div className="flex items-end gap-1 h-14">
                        {arr.map((v, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center justify-end">
                            <div
                              className={`w-full ${colors[i]} rounded-t`}
                              style={{ height: `${(v / max) * 100}%`, minHeight: v ? '4px' : '0' }}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-1 mt-1">
                        {labels.map((l, i) => (
                          <span key={i} className="flex-1 text-center text-[9px] text-slate-500">{l}</span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </Tile>

          {/* Piece 3 — Clarification Efficacy */}
          <Tile
            icon={MessageSquare} title="Clarification Efficacy" testId="tile-efficacy"
            status={efficacyStatus} infoKey="efficacy"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {efficacy._err ? (
              <p className="text-xs text-rose-400">{efficacy._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[efficacyStatus.color]}`} data-testid="efficacy-rate">
                  {efficacy.efficacy_rate == null ? '—' : `${(efficacy.efficacy_rate * 100).toFixed(1)}%`}
                </div>
                <p className="text-xs text-slate-500 mb-3">Of asks actually helped the user</p>
                <Stat label="Asks in window" value={efficacy.total_clarifications_asked ?? 0} />
                <Stat label="Helped (conf ↑)"       value={efficacy.outcomes?.helped ?? 0} />
                <Stat label="User reformulated"    value={efficacy.outcomes?.user_clarified ?? 0} />
                <Stat label="Unhelpful (stuck)"    value={efficacy.outcomes?.unhelpful ?? 0} />
                <Stat label="Abandoned (no retry)" value={efficacy.outcomes?.abandoned ?? 0} />
                {(efficacy.outcomes?.unknown ?? 0) > 0 && (
                  <Stat label="Unknown (legacy)" value={efficacy.outcomes.unknown} hint="pre-username traces" />
                )}
              </>
            )}
          </Tile>

          <Tile
            icon={Gauge} title="Cohesion Score" testId="tile-cohesion"
            status={cohesionStatus} infoKey="cohesion"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {cohesion._err ? (
              <p className="text-xs text-rose-400">{cohesion._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[cohesionStatus.color]}`} data-testid="cohesion-score">
                  {cohesion.per_workflow_avg_cohesion == null ? '—' : cohesion.per_workflow_avg_cohesion.toFixed(3)}
                </div>
                <Stat label="Workflows" value={cohesion.workflow_count ?? '—'} />
                <Stat label="Errored" value={cohesion.errored_workflow_count ?? '—'} />
                <Stat label="Avg latency" value={cohesion.avg_latency_ms ?? '—'} hint="ms" />
                <Stat label="P95 latency" value={cohesion.p95_latency_ms ?? '—'} hint="ms" />
              </>
            )}
          </Tile>

          <Tile
            icon={TrendingUp} title="Workflow Volume" testId="tile-workflow"
            status={workflowStatus} infoKey="workflow"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {workflow._err ? (
              <p className="text-xs text-rose-400">{workflow._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[workflowStatus.color]}`} data-testid="workflow-total">
                  {workflow.total_workflows ?? '—'}
                </div>
                <p className="text-xs text-slate-500 mb-3">Total workflows · 7d</p>
                <Stat label="Success rate" value={workflow.success_rate == null ? '—' : `${(workflow.success_rate * 100).toFixed(1)}%`} />
                <Stat label="Queries / hour" value={workflow.queries_per_hour ?? '—'} />
                <Stat label="P50 latency" value={workflow.p50_latency_ms ?? '—'} hint="ms" />
                <Stat label="P95 latency" value={workflow.p95_latency_ms ?? '—'} hint="ms" />
                <Stat label="P99 latency" value={workflow.p99_latency_ms ?? '—'} hint="ms" />
              </>
            )}
          </Tile>

          <Tile
            icon={Lock} title="Permissions" testId="tile-permissions"
            status={permissionsStatus} infoKey="permissions"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {permissions._err ? (
              <p className="text-xs text-rose-400">{permissions._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[permissionsStatus.color]}`} data-testid="block-rate">
                  {permissions.block_rate == null ? '—' : `${(permissions.block_rate * 100).toFixed(1)}%`}
                </div>
                <p className="text-xs text-slate-500 mb-3">Permission block rate</p>
                <Stat label="Total events" value={permissions.total_events ?? 0} />
                {(permissions.top_missing || []).slice(0, 3).map((m) => (
                  <Stat key={m.data_type} label={`Missing: ${m.data_type}`} value={m.count} />
                ))}
              </>
            )}
          </Tile>

          <Tile
            icon={Search} title="Retrieval Mix" testId="tile-retrieval"
            status={retrievalStatus} infoKey="retrieval"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {retrieval._err ? (
              <p className="text-xs text-rose-400">{retrieval._err}</p>
            ) : (
              <>
                <div className="text-4xl font-bold tabular-nums mb-3 text-cyan-400" data-testid="vector-count">
                  {retrieval.vector_embedding_count ?? '—'}
                </div>
                <p className="text-xs text-slate-500 mb-3">Document chunks indexed</p>
                {Object.entries(retrieval.tool_route_distribution || {}).slice(0, 5).map(([tool, count]) => (
                  <Stat key={tool} label={tool} value={count} />
                ))}
                {!Object.keys(retrieval.tool_route_distribution || {}).length && (
                  <p className="text-xs text-slate-500">No tool routes in window.</p>
                )}
              </>
            )}
          </Tile>

          <Tile
            icon={Activity} title="Per-Agent Latency" testId="tile-traces"
            status={latencyStatus} infoKey="latency"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {traces._err ? (
              <p className="text-xs text-rose-400">{traces._err}</p>
            ) : (
              <>
                <div className="text-4xl font-bold tabular-nums mb-3 text-cyan-400" data-testid="trace-total">
                  {traces.total_traces ?? '—'}
                </div>
                <p className="text-xs text-slate-500 mb-3">Total agent invocations</p>
                {sortBySequence(traces.per_agent || [], 'agent', AGENT_SEQUENCE_ORDER).map((row) => {
                  const lt = evalLight(row.avg_latency_ms || 0, { greenAt: 2000, yellowAt: 5000, direction: 'down' });
                  return (
                    <Stat
                      key={row.agent}
                      label={friendlyAgent(row.agent)}
                      value={`${Math.round(row.avg_latency_ms || 0)}`}
                      hint={`ms · ${row.count}`}
                      valueClass={LIGHT_TEXT[lt.color]}
                    />
                  );
                })}
                {!(traces.per_agent || []).length && (
                  <p className="text-xs text-slate-500">No agent traces yet.</p>
                )}
              </>
            )}
          </Tile>

          <Tile
            icon={BarChart3} title="Per-Agent-Role Cohesion" testId="tile-agent-kind"
            status={agentKindStatus} infoKey="agentKind"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {cohesion._err ? (
              <p className="text-xs text-rose-400">{cohesion._err}</p>
            ) : (
              <>
                {sortBySequence(cohesion.per_agent_kind || [], 'agent_kind', AGENT_KIND_SEQUENCE_ORDER).map((row) => {
                  const lt = evalLight(row.success_rate, { greenAt: 0.95, yellowAt: 0.8, direction: 'up' });
                  return (
                    <Stat
                      key={row.agent_kind}
                      label={friendlyKind(row.agent_kind)}
                      value={`${(row.success_rate * 100).toFixed(1)}%`}
                      hint={`${row.trace_count} traces · ${Math.round(row.avg_latency_ms)}ms`}
                      valueClass={LIGHT_TEXT[lt.color]}
                    />
                  );
                })}
                {!cohesion.per_agent_kind?.length && (
                  <p className="text-xs text-slate-500">No agent traces in window.</p>
                )}
              </>
            )}
          </Tile>

          <Tile
            icon={Trash2} title="Deletion Audit" testId="tile-deletion"
            status={deletionStatus} infoKey="deletion"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {deletion._err ? (
              <p className="text-xs text-rose-400">{deletion._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-3 ${LIGHT_TEXT[deletionStatus.color]}`} data-testid="deletion-total">
                  {deletion.total_deletions ?? 0}
                </div>
                <p className="text-xs text-slate-500 mb-3">Audit rows · 30d</p>
                {(deletion.by_subject_type || []).map((row) => (
                  <Stat
                    key={row.subject_type}
                    label={row.subject_type}
                    value={row.count}
                    hint={row.cascades ? `${row.cascades} cascades` : null}
                  />
                ))}
                {!(deletion.by_subject_type || []).length && (
                  <p className="text-xs text-slate-500">No deletions in window.</p>
                )}
              </>
            )}
          </Tile>

          <Tile
            icon={MousePointerClick} title="Beta User Events" testId="tile-user-events"
            status={userEventsStatus} infoKey="userEvents"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {userEvents._err ? (
              <p className="text-xs text-rose-400">{userEvents._err}</p>
            ) : (
              <>
                {/* SPD v5.91 (2026-02-16) — promote `unique_beta_visitors`
                    (real visitors, exempt operator/harness subtracted) to
                    the primary big-number slot. Previously the tile led
                    with `total_events` (clicks), so five operator test
                    accounts + 480 clicks read as a much bigger campaign
                    number than it actually was. Operator caught this on
                    2026-02-16: "these #'s don't look right — 5 users would
                    be me, I tested at least 3 or 4 different usernames +
                    Admin". Tile now shows real Beta traffic first, with
                    total clicks and raw union relegated to secondary.

                    Caption is gated on the presence of unique_beta_visitors
                    so a pre-v5.91 API rollback shows the honest
                    'Tracked clicks' label instead of mislabelling
                    total_events as 'Unique Beta visitors'. */}
                {typeof userEvents.unique_beta_visitors === 'number' ? (
                  <>
                    <div
                      className={`text-4xl font-bold tabular-nums mb-1 ${LIGHT_TEXT[userEventsStatus.color]}`}
                      data-testid="user-events-unique-beta-visitors"
                    >
                      {userEvents.unique_beta_visitors}
                    </div>
                    <p className="text-xs text-slate-500 mb-3">
                      Unique Beta visitors · last 7 days
                      {typeof userEvents.excluded_tenants_count === 'number' && userEvents.excluded_tenants_count > 0 && (
                        <span className="ml-1 text-slate-600">
                          ({userEvents.excluded_tenants_count} operator/harness excluded)
                        </span>
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <div
                      className={`text-4xl font-bold tabular-nums mb-1 ${LIGHT_TEXT[userEventsStatus.color]}`}
                      data-testid="user-events-unique-beta-visitors"
                    >
                      {userEvents.total_events ?? 0}
                    </div>
                    <p className="text-xs text-slate-500 mb-3">Tracked clicks · last 7 days</p>
                  </>
                )}
                <div className="mb-3 flex items-baseline gap-4 text-xs text-slate-500">
                  <span data-testid="user-events-total">
                    <span className="font-bold text-slate-300 tabular-nums">{userEvents.total_events ?? 0}</span> tracked clicks
                  </span>
                  {typeof userEvents.unique_tenants_union === 'number' && (
                    <span data-testid="user-events-union" title="Raw union — includes operator/harness tenants">
                      <span className="font-bold text-slate-300 tabular-nums">{userEvents.unique_tenants_union}</span> raw unique
                    </span>
                  )}
                </div>
                {/* SPD v5.71-fix (2026-02-11) — Iteration 65 caught that the
                    previous hard-coded 3-row .map() was invisible for the
                    five new whitelist events (page_view, atlas_visit,
                    query_submitted, comparison_evidence_open,
                    comparison_source_library_open). We now render every key
                    present in `by_event`, driven by a small label map so a
                    future whitelist extension surfaces automatically. Any
                    unknown key falls back to a humanised version of the raw
                    event name so nothing is silently dropped from the UI —
                    the exact silent-drop pattern SPD v5.71 was written to
                    close. */}
                {(() => {
                  const LABELS = {
                    accuracy_control_toggle: 'Accuracy Control toggled',
                    view_revenue_share: 'Revenue Share opened',
                    demo_excel_download: 'P&L Demo downloaded',
                    comparison_evidence_open: 'Evidence opened',
                    comparison_source_library_open: 'Source Library opened',
                    atlas_visit: 'Knowledge Atlas visited',
                    page_view: 'Page views',
                    query_submitted: 'Queries submitted',
                  };
                  const humanise = (k) =>
                    k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
                  const byEvent = userEvents.by_event || {};
                  // Preserve legacy row order first, then any new key alphabetically.
                  const preferred = [
                    'accuracy_control_toggle',
                    'view_revenue_share',
                    'demo_excel_download',
                    'atlas_visit',
                    'page_view',
                    'query_submitted',
                    'comparison_evidence_open',
                    'comparison_source_library_open',
                  ];
                  const seen = new Set();
                  const orderedKeys = [
                    ...preferred.filter((k) => byEvent[k] && !seen.has(k) && seen.add(k)),
                    ...Object.keys(byEvent).filter((k) => !preferred.includes(k)).sort(),
                  ];
                  if (!orderedKeys.length) {
                    // Legacy empty-state — no events at all.
                    return null;
                  }
                  return orderedKeys.map((k) => {
                    const bucket = byEvent[k] || {};
                    return (
                      <Stat
                        key={k}
                        label={LABELS[k] || humanise(k)}
                        value={bucket.count ?? 0}
                        hint={
                          // SPD v5.91 — prefer the exempt-filtered
                          // real-visitor count; fall back to the raw
                          // unique_tenants if the backend hasn't been
                          // upgraded yet (never nulls the UI).
                          bucket.unique_beta_visitors != null
                            ? `${bucket.unique_beta_visitors} unique`
                            : (bucket.unique_tenants ? `${bucket.unique_tenants} unique` : null)
                        }
                      />
                    );
                  });
                })()}
                {!userEvents.total_events && (
                  <p className="text-xs text-slate-500 mt-2">
                    No tracked clicks in window. Events fire on Control Centre toggles, Revenue Share opens, and P&amp;L Demo downloads.
                  </p>
                )}

                {/* SPD v5.91 (2026-02-16) — inline exempt-flag management.
                    Click to expand a list of every beta_users row so the
                    operator can flag their own test accounts and see the
                    unique-visitor count drop to the real number without
                    leaving the dashboard. Hardcoded EXEMPT_USERNAMES
                    (`startech`, `testharness_nonadmin`) are pre-excluded
                    server-side and cannot be toggled here. */}
                <div className="mt-4 border-t border-slate-800 pt-3">
                  <button
                    type="button"
                    onClick={() => setExemptOpen((o) => !o)}
                    className="flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-cyan-300 transition-colors"
                    data-testid="user-events-exempt-toggle"
                  >
                    <span className="tabular-nums">{exemptOpen ? '▾' : '▸'}</span>
                    <span>
                      Manage exempt accounts
                      {typeof userEvents.excluded_tenants_count === 'number' && (
                        <span className="ml-1 text-slate-600">
                          ({userEvents.excluded_tenants_count} currently excluded)
                        </span>
                      )}
                    </span>
                  </button>
                  {exemptOpen && (
                    <div className="mt-2 space-y-1.5" data-testid="user-events-exempt-panel">
                      {betaUsersErr && (
                        <p className="text-[11px] text-rose-400" data-testid="user-events-exempt-error">
                          {betaUsersErr}
                        </p>
                      )}
                      {betaUsers === null && !betaUsersErr && (
                        <p className="text-[11px] text-slate-500">Loading beta accounts…</p>
                      )}
                      {Array.isArray(betaUsers) && betaUsers.length === 0 && (
                        <p className="text-[11px] text-slate-500">No beta accounts registered.</p>
                      )}
                      {Array.isArray(betaUsers) && betaUsers.length > 0 && (
                        <ul className="max-h-48 overflow-y-auto space-y-0.5 text-[11px]">
                          {betaUsers.map((u) => {
                            const isMutating = !!mutatingExempt[u.username];
                            const isExempt = !!u.exempt;
                            return (
                              <li
                                key={u.username}
                                className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-slate-800/40"
                                data-testid={`user-events-exempt-row-${u.username}`}
                              >
                                <span className="truncate text-slate-300">
                                  {u.username}
                                  {u.is_admin && (
                                    <span className="ml-1.5 text-[10px] text-amber-400/80">admin</span>
                                  )}
                                  {!u.active && (
                                    <span className="ml-1.5 text-[10px] text-slate-500">revoked</span>
                                  )}
                                  {u.is_locked && (
                                    <span
                                      className="ml-1.5 text-[10px] text-rose-400/90"
                                      data-testid={`beta-user-locked-pill-${u.username}`}
                                    >
                                      locked
                                    </span>
                                  )}
                                </span>
                                <div className="flex items-center gap-2 shrink-0">
                                  {u.is_locked && (
                                    <button
                                      type="button"
                                      disabled={!!mutatingUnlock[u.username]}
                                      onClick={() => unlockBetaUser(u.username)}
                                      className="px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 text-[10px] font-medium hover:bg-rose-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                      data-testid={`beta-user-unlock-button-${u.username}`}
                                      title="Clear this user's lockout so they can log in again"
                                    >
                                      {mutatingUnlock[u.username] ? 'unlocking…' : 'unlock'}
                                    </button>
                                  )}
                                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                    <input
                                      type="checkbox"
                                      checked={isExempt}
                                      disabled={isMutating}
                                      onChange={(e) => toggleExempt(u.username, e.target.checked)}
                                      className="h-3 w-3 accent-cyan-500 cursor-pointer disabled:opacity-40"
                                      data-testid={`user-events-exempt-checkbox-${u.username}`}
                                    />
                                    <span className={isExempt ? 'text-cyan-300' : 'text-slate-500'}>
                                      {isMutating ? 'saving…' : 'exempt'}
                                    </span>
                                  </label>
                                  {/* IJ-259 · demo-admin toggle · grants
                                      access to /knowledge-controls ONLY.
                                      Flip on before a client demo · off after. */}
                                  <label
                                    className="flex items-center gap-1.5 cursor-pointer select-none"
                                    data-testid={`user-events-demo-admin-row-${u.username}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!u.is_demo_admin}
                                      disabled={!!mutatingDemoAdmin[u.username]}
                                      onChange={(e) => toggleDemoAdmin(u.username, e.target.checked)}
                                      className="h-3 w-3 accent-amber-400 cursor-pointer disabled:opacity-40"
                                      data-testid={`user-events-demo-admin-checkbox-${u.username}`}
                                    />
                                    <span className={u.is_demo_admin ? 'text-amber-300' : 'text-slate-500'}>
                                      {mutatingDemoAdmin[u.username] ? 'saving…' : 'demo'}
                                    </span>
                                  </label>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      <p className="text-[10px] text-slate-600 mt-1">
                        Flagged accounts still work normally — they are only removed from the &lsquo;Unique Beta visitors&rsquo; count.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </Tile>

          {/* Exit-Banner Analytics tile — 2026-02-13.
              Feeds off /api/admin/exit-banner/analytics which aggregates
              /api/exit-banner/dismissed events. Exempt users (admin +
              harness) are excluded from the counts so the operator sees
              real-visitor behaviour, not their own test traffic. */}
          <Tile
            icon={LogOut} title="Exit Banner (all-time)" testId="tile-exit-banner"
            status={
              exitBanner._err ? { color: 'rose', label: 'Error' } :
              ((exitBanner.total_dismissals ?? 0) === 0
                ? { color: 'slate', label: 'No data' }
                : { color: 'emerald', label: 'Tracking' })
            }
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {exitBanner._err ? (
              <p className="text-xs text-rose-400" data-testid="exit-banner-tile-error">{exitBanner._err}</p>
            ) : (
              <>
                <div className="text-4xl font-bold tabular-nums mb-3 text-slate-100" data-testid="exit-banner-total-dismissals">
                  {exitBanner.total_dismissals ?? 0}
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  [Got it] clicks · excludes admin + harness
                </p>
                <Stat
                  label="Leave-early dismissals"
                  value={exitBanner.by_variant?.leave_early ?? 0}
                  hint={
                    exitBanner.avg_session_duration_ms?.leave_early != null
                      ? `avg ${Math.round((exitBanner.avg_session_duration_ms.leave_early || 0) / 1000)}s in-session`
                      : null
                  }
                />
                <Stat
                  label="Completed dismissals"
                  value={exitBanner.by_variant?.completed ?? 0}
                  hint={
                    exitBanner.avg_session_duration_ms?.completed != null
                      ? `avg ${Math.round((exitBanner.avg_session_duration_ms.completed || 0) / 1000)}s in-session`
                      : null
                  }
                />
                <Stat label="Last 7 days" value={exitBanner.last_7_days ?? 0} />
                <Stat label="Last 24 hours" value={exitBanner.last_24_hours ?? 0} />
                {/* Tasks-completed histogram — 5 buckets (0-4). Shown
                    as a tiny row of bars so the operator can eyeball
                    "how far into the funnel did they get?" at a glance. */}
                <div className="mt-3">
                  <p className="text-xs text-slate-500 mb-1.5">Tasks completed on dismiss (0–4)</p>
                  {(() => {
                    const hist = exitBanner.tasks_completed_hist || {};
                    const max = Math.max(1, ...Object.values(hist).map((v) => Number(v) || 0));
                    return (
                      <div className="flex items-end gap-1 h-12" data-testid="exit-banner-tasks-hist">
                        {[0, 1, 2, 3, 4].map((k) => {
                          const n = Number(hist[String(k)] || 0);
                          const pct = (n / max) * 100;
                          return (
                            <div
                              key={k}
                              className="flex-1 flex flex-col items-center justify-end"
                              data-testid={`exit-banner-tasks-hist-bar-${k}`}
                            >
                              <div
                                className="w-full rounded-t bg-cyan-500/70"
                                style={{ height: `${pct}%`, minHeight: n > 0 ? '4px' : '1px' }}
                                title={`${n} dismissals with ${k} task${k === 1 ? '' : 's'} done`}
                              />
                              <span className="text-[10px] text-slate-500 mt-0.5 tabular-nums">{k}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                {exitBanner.last_dismissal_at && (
                  <p className="text-[10px] text-slate-600 mt-2 tabular-nums">
                    Last dismissal: {new Date(exitBanner.last_dismissal_at).toLocaleString('en-CA')}
                  </p>
                )}
              </>
            )}
          </Tile>

          {/* Financial tile — admin-only LLM spend + fixed-cost breakdown.
              record_llm_usage is now wired into LlmChat via the runtime
              monkey-patch at v3/integrations/ledger_patch.py, so every
              Anthropic call writes a row into v3_llm_cost_ledger. */}
          <Tile
            icon={LineChart} title="Financial (7d)" testId="tile-financial"
            status={
              financial._err ? { color: 'rose', label: 'Error' } :
              ((financial.totals?.total_usd ?? 0) === 0
                ? { color: 'amber', label: 'Ledger empty' }
                : { color: 'emerald', label: 'Tracking' })
            }
            infoKey="financial"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {financial._err ? (
              <p className="text-xs text-rose-400">{financial._err}</p>
            ) : (
              <>
                <div className="text-4xl font-bold tabular-nums mb-3 text-emerald-300" data-testid="financial-total-usd">
                  ${(financial.totals?.total_usd ?? 0).toFixed(4)}
                </div>
                <p className="text-xs text-slate-500 mb-3">LLM spend · last 7 days</p>
                <Stat label="Total queries" value={financial.totals?.total_queries ?? 0} />
                <Stat label="Input tokens"
                      value={(financial.totals?.total_input_tokens ?? 0).toLocaleString()} />
                <Stat label="Output tokens"
                      value={(financial.totals?.total_output_tokens ?? 0).toLocaleString()} />
                <Stat label="Avg cost / query"
                      value={`$${(financial.projections?.cost_per_query_usd ?? 0).toFixed(4)}`} />
                <Stat label="Daily avg"
                      value={`$${(financial.projections?.daily_avg_usd ?? 0).toFixed(4)}`} />
                <Stat label="30-day projection"
                      value={`$${(financial.projections?.monthly_projection_usd ?? 0).toFixed(2)}`} />

                {/* Per-model breakdown */}
                {financial.by_model && Object.keys(financial.by_model).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/40" data-testid="financial-by-model">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">By model</p>
                    {Object.entries(financial.by_model).map(([model, m]) => (
                      <div key={model} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-400 truncate pr-2">{model}</span>
                        <span className="tabular-nums text-slate-300">
                          {m.queries}× · ${m.usd.toFixed(4)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Daily trend sparkline (CSS bars — no chart lib) */}
                {financial.daily && financial.daily.length > 0 && (() => {
                  const maxUsd = Math.max(...financial.daily.map(d => d.usd || 0), 0.0001);
                  return (
                    <div className="mt-3 pt-3 border-t border-slate-700/40" data-testid="financial-daily-trend">
                      <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Daily trend</p>
                      <div className="flex items-end gap-1 h-16">
                        {financial.daily.map((d, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                            <div
                              className="w-full bg-emerald-500/40 group-hover:bg-emerald-400/70 rounded-sm transition-colors"
                              style={{ height: `${Math.max((d.usd / maxUsd) * 56, 2)}px` }}
                              title={`${d.date}: $${d.usd.toFixed(4)} · ${d.queries} queries`}
                            />
                            <span className="text-[9px] text-slate-500 tabular-nums">
                              {d.date.slice(5)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Pricing snapshot expandable */}
                {financial.pricing_snapshot && (
                  <details className="mt-3 pt-3 border-t border-slate-700/40 group" data-testid="financial-pricing-details">
                    <summary className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 cursor-pointer hover:text-slate-300">
                      Pricing used <span className="text-cyan-400 group-open:hidden">[show]</span><span className="text-cyan-400 hidden group-open:inline">[hide]</span>
                    </summary>
                    <p className="text-[10px] text-slate-500 mb-1.5">USD per 1M tokens — Anthropic billing currency · input / output</p>
                    {Object.entries(financial.pricing_snapshot).map(([model, rate]) => (
                      <div key={model} className="flex justify-between text-xs py-0.5">
                        <span className="text-slate-400 truncate pr-2">{model}</span>
                        <span className="tabular-nums text-slate-300">
                          ${rate.input}/${rate.output}
                        </span>
                      </div>
                    ))}
                  </details>
                )}

                <div className="mt-3 pt-3 border-t border-slate-700/40">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Fixed costs</p>
                  {(financial.fixed_costs || []).map((c, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span className="text-slate-400 truncate pr-2">{c.name}</span>
                      <span className="tabular-nums text-slate-300">
                        {c.monthly_usd === 0 ? 'Free' : `$${c.monthly_usd.toFixed(2)}/mo`}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Export raw ledger as CSV for audit */}
                <button
                  type="button"
                  data-testid="financial-export-csv"
                  onClick={async () => {
                    const token = localStorage.getItem('startech_token');
                    try {
                      const res = await fetch(`${API}/api/v1/admin/financial-ledger.csv?days=7`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!res.ok) throw new Error(`Export failed (${res.status})`);
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `financial-ledger-7d-${new Date().toISOString().slice(0,10)}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      alert(`CSV export failed: ${e.message}`);
                    }
                  }}
                  className="mt-3 w-full text-xs py-1.5 px-3 rounded border border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30 transition-colors"
                >
                  Export ledger CSV (audit)
                </button>

                {(financial.totals?.total_queries ?? 0) === 0 && (
                  <p className="text-[11px] text-amber-400/80 mt-3">
                    LLM cost ledger empty. Submit a query to populate.
                  </p>
                )}
              </>
            )}
          </Tile>

          <Tile
            icon={AlertCircle} title="Recent Pipeline Errors" testId="tile-errors"
            status={errorsStatus} infoKey="pipelineErrors"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {errors._err ? (
              <p className="text-xs text-rose-400">{errors._err}</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-1 ${LIGHT_TEXT[errorsStatus.color]}`} data-testid="error-count">
                  {typeof errors.recent_count === 'number' ? errors.recent_count : (errors.count ?? 0)}
                </div>
                <p className="text-xs text-slate-500 mb-1" data-testid="error-window-label">
                  errors in last hour
                </p>
                <p className="text-[10px] text-slate-600 mb-3" data-testid="error-total-label">
                  ({errors.count ?? 0} in last 20 · intentional security blocks excluded)
                </p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(errors.errors || []).slice(0, 8).map((e, i) => (
                    <div key={`${e.agent}-${e.created_at}-${i}`} className="text-xs border-l-2 border-rose-700 pl-2">
                      <div className="text-rose-300 font-medium truncate" title={e.agent}>
                        {friendlyAgent(e.agent)}
                      </div>
                      <div className="text-slate-400 truncate" title={e.error || ''}>
                        {(e.error || 'unknown error').slice(0, 80)}
                      </div>
                      <div className="text-[10px] text-slate-600">
                        {friendlyKind(e.agent_kind)} · {e.latency_ms}ms
                      </div>
                    </div>
                  ))}
                  {!(errors.errors || []).length && (
                    <p className="text-xs text-emerald-400">No errors recorded — pipeline is clean.</p>
                  )}
                </div>
              </>
            )}
          </Tile>

          <Tile
            icon={GitBranch} title="Release Gates" testId="tile-releases"
            status={releasesStatus} infoKey="releases"
            openInfoKey={openInfoKey} setOpenInfoKey={setOpenInfoKey}
          >
            {releases._err ? (
              <p className="text-xs text-rose-400">{releases._err}</p>
            ) : !releases.summary ? (
              <p className="text-xs text-slate-500">No release-gate data yet.</p>
            ) : (
              <>
                <div className={`text-4xl font-bold tabular-nums mb-1 ${LIGHT_TEXT[releasesStatus.color]}`} data-testid="release-passing">
                  {releases.summary.passing ?? 0}/{releases.summary.actionable_total ?? 0}
                </div>
                <p className="text-xs text-slate-500 mb-3" data-testid="release-overall">
                  Actionable MVP gates · {releases.summary.overall_pass ? 'overall PASS' : 'overall FAIL'}
                </p>
                {(releases.gates || []).slice(0, 6).map((g) => (
                  <Stat
                    key={g.id}
                    label={(g.id || '').replace(/_/g, ' ')}
                    value={g.pass ? '✓' : '✗'}
                    hint={g.threshold}
                    valueClass={g.pass ? 'text-emerald-400' : 'text-rose-400'}
                  />
                ))}
                <p className="text-[10px] text-slate-600 mt-2">
                  + {releases.summary.informational_total ?? 0} informational gates
                </p>
              </>
            )}
          </Tile>
        </div>

        {/* SPD v5.92 (2026-07-18) — "How I'm Doing" governance tile.
            Live scoreboard of agent adherence + Judge scrutiny for
            the current UTC calendar-day session. Mounted directly ABOVE
            Security Health per operator-signed mockup v4. Copy for all
            visible strings sourced from mockup — [COPY-APPROVED:
            governance-tile-v4]. Traffic light rule: any user-facing miss
            = red; internal-only loose ends = amber; clean = green. */}
        <div className="mt-8">
          <GovernanceTile />
          <AdminGovernance />
          <AdminAtlasRepair />
        </div>

        {/* SPD v5.72 (2026-02-11) — Security Health tile.
            Read-only aggregation of existing v3/security/* signals. Uses
            the same 30s poll as every other admin tile. Traffic-light
            aggregate (green/amber/red) computed server-side from 7
            individual signals. Placed BEFORE the "Security Snapshots"
            historical tile so an operator's eye reads live-health first,
            then dives into audit history if amber/red. */}
        <div className="mt-8 p-5 bg-slate-900/70 rounded-xl border border-slate-800" data-testid="tile-security-health">
          <div className="flex items-start justify-between mb-4 gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck
                className={
                  securityHealth.overall_status === 'red' ? 'text-rose-400'
                  : securityHealth.overall_status === 'amber' ? 'text-amber-400'
                  : 'text-emerald-400'
                }
                size={18}
              />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-200">
                Security Health · Live signals
              </h2>
              <span
                className={
                  'ml-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ' +
                  (securityHealth.overall_status === 'red'
                    ? 'bg-rose-500/15 text-rose-300 border border-rose-500/40'
                    : securityHealth.overall_status === 'amber'
                    ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40'
                    : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40')
                }
                data-testid="security-health-overall-badge"
              >
                <span
                  className={
                    'w-1.5 h-1.5 rounded-full ' +
                    (securityHealth.overall_status === 'red'
                      ? 'bg-rose-400 animate-pulse'
                      : securityHealth.overall_status === 'amber'
                      ? 'bg-amber-400 animate-pulse'
                      : 'bg-emerald-400')
                  }
                />
                {securityHealth.overall_status || 'unknown'}
              </span>
            </div>
            <span className="text-[11px] text-slate-500" data-testid="security-health-last-computed">
              {securityHealth.last_computed_at
                ? `Computed ${new Date(securityHealth.last_computed_at).toLocaleTimeString()}`
                : 'not yet loaded'}
            </span>
          </div>
          {securityHealth._err ? (
            <p className="text-xs text-rose-400" data-testid="security-health-error">{securityHealth._err}</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="security-health-signals">
                {(securityHealth.signals || []).map((sig) => {
                  const cls =
                    sig.status === 'red' ? 'border-rose-500/40 bg-rose-500/5'
                    : sig.status === 'amber' ? 'border-amber-500/40 bg-amber-500/5'
                    : 'border-slate-800 bg-slate-950/60';
                  const dot =
                    sig.status === 'red' ? 'bg-rose-400'
                    : sig.status === 'amber' ? 'bg-amber-400'
                    : 'bg-emerald-400';
                  const valColour =
                    sig.status === 'red' ? 'text-rose-200'
                    : sig.status === 'amber' ? 'text-amber-200'
                    : 'text-cyan-100';
                  return (
                    <div
                      key={sig.id}
                      className={`p-3 rounded-lg border ${cls}`}
                      data-testid={`security-health-signal-${sig.id}`}
                      title={sig.detail || ''}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] uppercase tracking-widest text-slate-400">
                          {sig.label}
                        </span>
                        <span className={`w-2 h-2 rounded-full ${dot}`} />
                      </div>
                      <div className={`text-lg font-bold ${valColour}`}>
                        {sig.value === null || sig.value === undefined ? '—' : String(sig.value)}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                        {sig.detail}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* IJ-024 (2026-07-18) — attacker-visibility sub-sections.
                  Read-only display of the SPD v5.93 payload from
                  /api/admin/security-health. Each section only renders
                  when its array has rows (or the spray flag is true),
                  keeping the tile compact when everything is quiet. */}
              {securityHealth.credential_spray_detected && (securityHealth.credential_spray_ips || []).length > 0 && (
                <div
                  className="mt-4 p-3 rounded-lg border border-rose-500/50 bg-rose-500/10"
                  data-testid="security-health-credential-spray-alert"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
                    <h3 className="text-[11px] font-bold uppercase tracking-widest text-rose-200">
                      Credential-spray alert · one IP hitting many usernames
                    </h3>
                  </div>
                  <table className="w-full text-xs" data-testid="security-health-spray-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-wider text-rose-300/70">
                        <th className="py-1 pr-4 font-medium">Attacking IP (hashed)</th>
                        <th className="py-1 pr-4 font-medium">Usernames hit</th>
                        <th className="py-1 font-medium">Total attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {securityHealth.credential_spray_ips.map((row) => (
                        <tr
                          key={row.ip_hash}
                          className="border-t border-rose-500/20"
                          data-testid={`security-health-spray-row-${row.ip_hash}`}
                        >
                          <td className="py-1 pr-4 font-mono text-rose-100">{row.ip_hash}</td>
                          <td className="py-1 pr-4 tabular-nums text-rose-100 font-bold">{row.distinct_usernames}</td>
                          <td className="py-1 tabular-nums text-rose-200">{row.total_attempts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="mt-2 text-[10px] text-rose-300/70 leading-snug">
                    A single IP address is trying to log in as three or more different
                    usernames in the last 24 hours. This is the classic pattern of a
                    credential-spray attack. See the incident-response ladder for the
                    escalation steps.
                  </p>
                </div>
              )}

              {(securityHealth.locked_accounts || []).length > 0 && (
                <div className="mt-4" data-testid="security-health-locked-accounts">
                  <h3 className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                    Currently locked accounts
                  </h3>
                  <div className="rounded-lg border border-slate-800 bg-slate-950/60 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/60">
                          <th className="py-1.5 px-3 font-medium">Username</th>
                          <th className="py-1.5 px-3 font-medium">Failed attempts</th>
                          <th className="py-1.5 px-3 font-medium">Locked until</th>
                          <th className="py-1.5 px-3 font-medium">Last failure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {securityHealth.locked_accounts.map((row, idx) => (
                          <tr
                            key={`${row.username}-${idx}`}
                            className="border-t border-slate-800"
                            data-testid={`security-health-locked-row-${row.username}`}
                          >
                            <td className="py-1.5 px-3 font-mono text-slate-200">{row.username}</td>
                            <td className="py-1.5 px-3 tabular-nums text-amber-200">{row.failed_attempts}</td>
                            <td className="py-1.5 px-3 text-slate-400 text-[11px]">
                              {row.locked_until ? new Date(row.locked_until).toLocaleString() : '—'}
                            </td>
                            <td className="py-1.5 px-3 text-slate-400 text-[11px]">
                              {row.last_failure_at ? new Date(row.last_failure_at).toLocaleString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(securityHealth.top_attacking_ips_24h || []).length > 0 && (
                <div className="mt-4" data-testid="security-health-top-ips">
                  <h3 className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                    Top attacking IPs · last 24 hours
                  </h3>
                  <div className="rounded-lg border border-slate-800 bg-slate-950/60 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/60">
                          <th className="py-1.5 px-3 font-medium">IP address (hashed)</th>
                          <th className="py-1.5 px-3 font-medium">Failed attempts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {securityHealth.top_attacking_ips_24h.map((row) => (
                          <tr
                            key={row.ip_hash}
                            className="border-t border-slate-800"
                            data-testid={`security-health-top-ip-row-${row.ip_hash}`}
                          >
                            <td className="py-1.5 px-3 font-mono text-slate-200">{row.ip_hash}</td>
                            <td className="py-1.5 px-3 tabular-nums text-amber-200">{row.failed_attempts}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-1.5 text-[10px] text-slate-500 leading-snug">
                    IP addresses are one-way hashed at write time; only the truncated fingerprint
                    is stored. Rows self-purge after 24 hours.
                  </p>
                </div>
              )}

              <p className="mt-4 text-[10px] text-slate-500 leading-relaxed">
                Signals refresh every 30 seconds along with the rest of the dashboard.
                Red badges indicate an intrusion signal that warrants immediate action; amber
                indicates elevated pressure worth watching. See <span className="font-mono text-slate-400">/app/memory/INCIDENT_RESPONSE.md</span> for
                the response ladder.
              </p>
            </>
          )}
        </div>

        {/* SPD v5.70 — Security snapshots panel (2026-07-11) */}
        <div className="mt-8 p-5 bg-slate-900/70 rounded-xl border border-slate-800" data-testid="tile-security-snapshots">
          <div className="flex items-start justify-between mb-4 gap-2">
            <div className="flex items-center gap-2">
              <GitBranch className="text-cyan-400" size={18} />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-200">
                Security Snapshots · Pre-refactor state
              </h2>
            </div>
            <span className="text-[11px] text-slate-500">
              {security.count || 0} recorded
            </span>
          </div>
          {security._err ? (
            <p className="text-xs text-rose-400" data-testid="security-snapshots-error">{security._err}</p>
          ) : (security.snapshots || []).length === 0 ? (
            <p className="text-xs text-slate-500">No snapshots recorded yet.</p>
          ) : (
            <div className="space-y-3" data-testid="security-snapshots-list">
              {(security.snapshots || []).map((s) => {
                const scans = s.scans || {};
                const secrets = scans.secrets_sweep || {};
                const yarn = scans.frontend_yarn_audit || {};
                const pip = scans.backend_pip_audit || {};
                const sev = yarn.severity_counts || {};
                return (
                  <div key={s.id} className="p-3 bg-slate-950/60 rounded-lg border border-slate-800" data-testid={`security-snapshot-${s.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-sm font-semibold text-cyan-100">{s.label}</div>
                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                          {s.created_at} · git {(s.git_head_sha || '').slice(0, 12)} · tag {s.git_tag}
                        </div>
                      </div>
                      <div className="text-right text-[10px] text-slate-400">
                        <div>MongoDB dump</div>
                        <div className="font-mono text-cyan-300">{Math.round((s.mongo_dump_size_bytes || 0) / (1024*1024))} MB · {s.mongo_collections_count} collections</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-3 text-[11px]">
                      <div>
                        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Secrets sweep</div>
                        <div className={secrets.verdict === 'CLEAN' ? 'text-emerald-400 font-semibold' : 'text-rose-400 font-semibold'}>
                          {secrets.verdict || 'unknown'}
                        </div>
                        <div className="text-slate-500 mt-0.5">
                          0 AWS · 0 keys · 0 tokens · 0 hardcoded passwords
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Frontend CVE (yarn audit)</div>
                        <div className={yarn.verdict === 'CLEAN' ? 'text-emerald-400 font-semibold' : 'text-amber-400 font-semibold'}>
                          {yarn.total_advisories || 0} advisories
                        </div>
                        <div className="text-slate-500 mt-0.5">
                          <span className="text-rose-400">{sev.critical || 0} crit</span> · <span className="text-rose-300">{sev.high || 0} high</span> · <span className="text-amber-300">{sev.moderate || 0} mod</span> · {sev.low || 0} low
                        </div>
                      </div>
                      <div>
                        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Backend CVE (pip-audit)</div>
                        <div className="text-slate-400 font-semibold">
                          {pip.verdict || 'unknown'}
                        </div>
                        <div className="text-slate-500 mt-0.5">
                          {pip.reason ? String(pip.reason).slice(0, 80) : ''}
                        </div>
                      </div>
                    </div>
                    {yarn.top_offenders_by_module && (
                      <div className="mt-3 pt-3 border-t border-slate-800">
                        <div className="text-slate-500 uppercase tracking-wider text-[9px] mb-1">Top offenders</div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(yarn.top_offenders_by_module).slice(0, 10).map(([mod, c]) => (
                            <span key={mod} className="px-2 py-0.5 rounded bg-slate-800 text-[10px] font-mono text-cyan-200">
                              {mod}
                              {c.critical > 0 && <span className="text-rose-400 ml-1">c{c.critical}</span>}
                              {c.high > 0 && <span className="text-rose-300 ml-1">h{c.high}</span>}
                              {c.moderate > 0 && <span className="text-amber-300 ml-1">m{c.moderate}</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-2 text-[10px] font-mono text-slate-600">
                      Local artefact path: {s.mongo_dump_path}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Piece 4 — Composite correlation time-series.
            One view overlaying confidence (leading), clarifications (mediating),
            feedback (lagging) — lets the team see the causal chain on one axis. */}
        <div className="mt-8 p-5 bg-slate-900/70 rounded-xl border border-slate-800" data-testid="tile-correlation">
          <div className="flex items-start justify-between mb-4 gap-2">
            <div className="flex items-center gap-2">
              <LineChart className="text-cyan-400" size={18} />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-200">
                Confidence × Clarifications × Feedback — daily correlation
              </h2>
              <button
                onClick={() => setOpenInfoKey(openInfoKey === 'correlation' ? null : 'correlation')}
                className="text-slate-500 hover:text-cyan-400 transition"
                data-testid="tile-correlation-info-btn"
                aria-label="What this shows"
              >
                <Info size={14} />
              </button>
            </div>
            <div className="flex items-center gap-1.5 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-cyan-400 inline-block"></span>Confidence</span>
              <span className="text-slate-700">·</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-amber-400 inline-block"></span>Clarification rate</span>
              <span className="text-slate-700">·</span>
              <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block"></span>👍 ratio</span>
            </div>
          </div>

          {openInfoKey === 'correlation' && (
            <InfoPanel
              info={INFO_COPY.correlation}
              onClose={() => setOpenInfoKey(null)}
              testIdRoot="tile-correlation"
            />
          )}

          {correlation._err ? (
            <p className="text-xs text-rose-400">{correlation._err}</p>
          ) : (
            (() => {
              const series = correlation.series || [];
              if (!series.length) {
                return <p className="text-xs text-slate-500">No data yet — try widening the window.</p>;
              }
              // Shared 0..1 y-axis; missing values skipped in path.
              const width = 900, height = 140, padL = 32, padR = 16, padT = 8, padB = 24;
              const innerW = width - padL - padR;
              const innerH = height - padT - padB;
              const xAt = (i) => padL + (series.length > 1 ? (i / (series.length - 1)) * innerW : innerW / 2);
              const yAt = (v) => padT + (1 - v) * innerH;
              const buildPath = (key) => {
                const pts = series.map((d, i) => [i, d[key]]).filter(([, v]) => v != null);
                if (!pts.length) return '';
                return pts.map(([i, v], idx) => `${idx === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
              };
              const confPath = buildPath('mean_confidence');
              const clarPath = buildPath('clarification_rate');
              const fbPath = buildPath('feedback_ratio');
              return (
                <div className="w-full overflow-x-auto" data-testid="correlation-chart">
                  <svg viewBox={`0 0 ${width} ${height}`} className="w-full">
                    {/* gridlines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((t) => (
                      <g key={t}>
                        <line x1={padL} x2={padL + innerW} y1={yAt(t)} y2={yAt(t)} stroke="#1e293b" strokeWidth={1} />
                        <text x={padL - 6} y={yAt(t) + 3} textAnchor="end" fontSize="9" fill="#475569">{t.toFixed(2)}</text>
                      </g>
                    ))}
                    {/* x-axis labels (first / mid / last) */}
                    {series.map((d, i) => (
                      (i === 0 || i === series.length - 1 || i === Math.floor(series.length / 2)) && (
                        <text key={i} x={xAt(i)} y={height - 6} textAnchor="middle" fontSize="9" fill="#64748b">
                          {d.date.slice(5)}
                        </text>
                      )
                    ))}
                    {/* lines */}
                    {confPath && <path d={confPath} stroke="#22d3ee" strokeWidth={2} fill="none" strokeLinejoin="round" />}
                    {clarPath && <path d={clarPath} stroke="#f59e0b" strokeWidth={2} fill="none" strokeLinejoin="round" strokeDasharray="4 2" />}
                    {fbPath && <path d={fbPath} stroke="#34d399" strokeWidth={2} fill="none" strokeLinejoin="round" strokeDasharray="2 2" />}
                  </svg>
                  {/* Compact data table */}
                  <div className="mt-3 grid grid-cols-4 gap-2 text-[10px] text-slate-400">
                    <div className="font-semibold text-slate-500">Day</div>
                    <div className="font-semibold text-slate-500 text-right">Confidence</div>
                    <div className="font-semibold text-slate-500 text-right">Clarif. rate</div>
                    <div className="font-semibold text-slate-500 text-right">👍 ratio</div>
                    {series.map((d) => (
                      <React.Fragment key={d.date}>
                        <div className="font-mono">{d.date.slice(5)}</div>
                        <div className="text-right tabular-nums text-cyan-400">{d.mean_confidence == null ? '—' : d.mean_confidence.toFixed(2)}</div>
                        <div className="text-right tabular-nums text-amber-400">{d.clarification_rate == null ? '—' : `${(d.clarification_rate * 100).toFixed(0)}%`}</div>
                        <div className="text-right tabular-nums text-emerald-400">{d.feedback_ratio == null ? '—' : `${(d.feedback_ratio * 100).toFixed(0)}%`}</div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })()
          )}
        </div>

        <div className="mt-8 pt-6 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-500">
          <Database size={12} />
          <span>v3 telemetry · cohesion BRD §5.1 · permissions BRD §8 · retrieval BRD §EntityMapV32 · accuracy v0 (heuristic) · confidence + efficacy + correlation Feb 2026</span>
        </div>

        {/* Controlled Documents vault — secure admin-only, audit-logged file access */}
        <ControlledDocs />
      </div>
    </div>
  );
}
